import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as signalR from '@microsoft/signalr'
import { releaseApi } from '../api'
import type {
    WorkItemSummary,
    WorkItemDetail,
    StartWorkResult,
    AddToReleaseResult,
    SprintStatusResponse,
    SprintReleasePipeline,
    AddSprintToReleaseResult,
    SprintHistorySummary,
    SprintTestPlan,
    ReleaseDevSyncStatus,
    SprintSessionChunk,
    PendingQueueEntry,
    PendingQueueResponse,
} from '../api'
import { useEscapeKey } from '@marque/ui'
import { PageDescription } from '../components/PageDescription'
import { WorkItemDetailModal } from '../components/WorkItemDetailModal'
import { BoardDeliveryStatus, BoardEpicGroup } from '../components/BoardDeliveryStatus'
import { groupBoardItems } from '../components/boardDelivery'

type ViewMode = 'list' | 'board'

// ── Helpers ───────────────────────────────────────────────────────────────────

type WorkItemTabId = 'parking-lot' | 'backlog' | 'in-progress' | 'ready'
type TabId = WorkItemTabId | 'history' | 'sprint'

const TABS: { id: TabId; label: string }[] = [
    { id: 'parking-lot',  label: 'Parking Lot' },
    { id: 'backlog',      label: 'Backlog' },
    { id: 'sprint',       label: 'Sprint' },
    { id: 'in-progress',  label: 'In Progress' },
    { id: 'ready',        label: 'Ready to Release' },
    { id: 'history',      label: 'Sprint History' },
]

function getBranchName(item: WorkItemSummary): string {
    return item.workItemType === 'Bug'
        ? `hotfix/AB#${item.id}`
        : `feature/AB#${item.id}`
}

function relativeAge(iso: string | null): string {
    if (!iso) return ''
    const diff = Date.now() - new Date(iso).getTime()
    const days = Math.floor(diff / 86_400_000)
    if (days === 0) return 'today'
    if (days === 1) return '1d ago'
    if (days < 30)  return `${days}d ago`
    const wks = Math.floor(days / 7)
    return `${wks}w ago`
}

const TYPE_STYLES: Record<string, string> = {
    'Bug':        'bg-red-500/15 text-red-400 ring-1 ring-red-500/25',
    'User Story': 'bg-blue-500/15 text-blue-400 ring-1 ring-blue-500/25',
    'Epic':       'bg-purple-500/15 text-purple-400 ring-1 ring-purple-500/25',
}
function typeStyle(t: string) {
    return TYPE_STYLES[t] ?? 'bg-slate-500/15 text-slate-400 ring-1 ring-slate-500/25'
}

const PRIORITY_STYLES: Record<number, string> = {
    1: 'text-red-400',
    2: 'text-amber-400',
    3: 'text-slate-400',
    4: 'text-slate-600',
}
function priorityLabel(p: number | null) {
    if (!p) return null
    const colours = PRIORITY_STYLES[p] ?? 'text-slate-600'
    return <span className={`text-xs font-bold ${colours}`}>P{p}</span>
}

// ── Tag helpers ───────────────────────────────────────────────────────────────

function hasTag(tags: string | null, tag: string): boolean {
    if (!tags) return false
    return tags.split(';').some(t => t.trim().toLowerCase() === tag.toLowerCase())
}

function SpecBadge() {
    return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30 shrink-0 whitespace-nowrap">
            🔬 Spec
        </span>
    )
}

// ── Area badges ───────────────────────────────────────────────────────────────

interface AreaBadge { label: string; style: string }

const AREA_BADGE: Record<string, AreaBadge> = {
    ops:        { label: 'Ops',        style: 'bg-violet-500/20 text-violet-300 ring-1 ring-violet-500/30' },
    central:    { label: 'Central',    style: 'bg-blue-500/20 text-blue-300 ring-1 ring-blue-500/30' },
    tenant:     { label: 'Tenant',     style: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30' },
}

const MODULE_BADGE: Record<string, AreaBadge> = {
    finance:    { label: 'Finance',    style: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30' },
    ar:         { label: 'Finance',    style: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30' },
    ap:         { label: 'Finance',    style: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30' },
    gl:         { label: 'Finance',    style: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30' },
    sales:      { label: 'Sales',      style: 'bg-indigo-500/20 text-indigo-300 ring-1 ring-indigo-500/30' },
    purchasing: { label: 'Purchasing', style: 'bg-orange-500/20 text-orange-300 ring-1 ring-orange-500/30' },
    inventory:  { label: 'Inventory',  style: 'bg-lime-500/20 text-lime-300 ring-1 ring-lime-500/30' },
    stock:      { label: 'Inventory',  style: 'bg-lime-500/20 text-lime-300 ring-1 ring-lime-500/30' },
    plm:        { label: 'PLM',        style: 'bg-pink-500/20 text-pink-300 ring-1 ring-pink-500/30' },
    auth:       { label: 'Auth',       style: 'bg-slate-500/20 text-slate-300 ring-1 ring-slate-500/30' },
}

const MODULE_PRIORITY = ['finance', 'ar', 'ap', 'gl', 'sales', 'purchasing', 'inventory', 'stock', 'plm', 'auth']

function getAreaBadges(tags: string | null): AreaBadge[] {
    if (!tags) return []
    const tagSet = new Set(tags.split(';').map(t => t.trim().toLowerCase()))
    const badges: AreaBadge[] = []

    // Area badges — show all that match (an item may be tagged both 'ops' and 'central')
    for (const key of ['ops', 'central', 'tenant']) {
        const b = AREA_BADGE[key]
        if (tagSet.has(key) && b) { badges.push(b) }
    }

    // Module — first match wins
    for (const key of MODULE_PRIORITY) {
        if (tagSet.has(key) && MODULE_BADGE[key]) {
            // Avoid duplicate label if area badge already says the same thing
            const badge = MODULE_BADGE[key]
            if (!badges.some(b => b.label === badge.label)) {
                badges.push(badge)
            }
            break
        }
    }

    return badges
}

// ── Area override (localStorage) ─────────────────────────────────────────────

const AREA_OVERRIDE_KEY = 'marque-area-overrides-v1'


interface AreaOverrideCtx {
    overrides: Record<number, string[]>
    setOverride: (id: number, labels: string[] | null) => void
}

const AreaOverrideContext = createContext<AreaOverrideCtx>({ overrides: {}, setOverride: () => undefined })

function AreaOverrideProvider({ children }: { children: ReactNode }) {
    const [overrides, setOverrides] = useState<Record<number, string[]>>(() => {
        try { return JSON.parse(localStorage.getItem(AREA_OVERRIDE_KEY) ?? '{}') as Record<number, string[]> }
        catch { return {} }
    })

    function setOverride(id: number, labels: string[] | null) {
        setOverrides(prev => {
            const next = { ...prev }
            if (!labels || labels.length === 0) { delete next[id] } else { next[id] = labels }
            localStorage.setItem(AREA_OVERRIDE_KEY, JSON.stringify(next))
            return next
        })
    }

    return <AreaOverrideContext.Provider value={{ overrides, setOverride }}>{children}</AreaOverrideContext.Provider>
}

// ── Work item detail modal context ────────────────────────────────────────────

const WorkItemModalContext = createContext<(id: number) => void>(() => undefined)

function WorkItemModalProvider({ children }: { children: ReactNode }) {
    const [selectedId, setSelectedId] = useState<number | null>(null)
    return (
        <WorkItemModalContext.Provider value={setSelectedId}>
            {children}
            <WorkItemDetailModal workItemId={selectedId} onClose={() => setSelectedId(null)} />
        </WorkItemModalContext.Provider>
    )
}

// ── Start Work Modal ──────────────────────────────────────────────────────────

interface StartWorkModalProps {
    item: WorkItemSummary
    onConfirm: () => void
    onCancel: () => void
    isPending: boolean
    result: StartWorkResult | null
}

function StartWorkModal({ item, onConfirm, onCancel, isPending, result }: StartWorkModalProps) {
    useEscapeKey(onCancel)
    const branchName = getBranchName(item)

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl p-6 w-[420px] flex flex-col gap-4">
                {result ? (
                    <>
                        <div className="flex items-center gap-3">
                            <span className="text-emerald-400 text-xl">✓</span>
                            <h2 className="text-base font-semibold text-white">Work started</h2>
                        </div>
                        <div className="rounded-lg bg-slate-900/60 border border-slate-700 p-3 flex flex-col gap-2">
                            <p className="text-xs text-slate-400">Branch created</p>
                            <p className="text-sm font-mono text-emerald-300">{result.branchName}</p>
                        </div>
                        {result.workItemUrl ? (
                            <p className="text-xs text-slate-400">
                                Work item transitioned to <strong className="text-slate-200">Active</strong>.{' '}
                                <a
                                    href={result.workItemUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-400 underline hover:text-blue-300"
                                >
                                    View in ADO ↗
                                </a>
                            </p>
                        ) : (
                            <p className="text-xs text-slate-400">
                                Work item transitioned to <strong className="text-slate-200">Active</strong>.
                            </p>
                        )}
                        <div className="flex justify-end">
                            <button
                                onClick={onCancel}
                                className="rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-white hover:bg-slate-600 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div>
                            <h2 className="text-base font-semibold text-white">Start work on AB#{item.id}</h2>
                            <p className="mt-1 text-sm text-slate-400 line-clamp-2">{item.title}</p>
                        </div>

                        <div className="rounded-lg bg-slate-900/60 border border-slate-700 p-3 flex flex-col gap-1.5">
                            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Branch to create</p>
                            <p className="text-sm font-mono text-slate-200">{branchName}</p>
                            <p className="text-xs text-slate-500">from main in the source repository</p>
                        </div>

                        <p className="text-xs text-slate-400">
                            The work item will be transitioned to <strong className="text-slate-200">Active</strong>{' '}
                            and assigned to you.
                        </p>

                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={onCancel}
                                disabled={isPending}
                                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onConfirm}
                                disabled={isPending}
                                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 transition-colors disabled:opacity-50"
                            >
                                {isPending ? 'Starting…' : 'Start Work'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

// ── Add to Release Modal ──────────────────────────────────────────────────────

interface AddToReleaseModalProps {
    item: WorkItemSummary
    onConfirm: () => void
    onCancel: () => void
    isPending: boolean
    result: AddToReleaseResult | null
}

function AddToReleaseModal({ item, onConfirm, onCancel, isPending, result }: AddToReleaseModalProps) {
    useEscapeKey(onCancel)
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '.')
    const releaseBranch = result?.releaseBranch ?? `release/${today}`

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl p-6 w-[420px] flex flex-col gap-4">
                {result ? (
                    <>
                        {result.cherryPickStatus === 'succeeded' || result.cherryPickStatus === 'already-merged' ? (
                            <div className="flex items-center gap-3">
                                <span className="text-emerald-400 text-xl">✓</span>
                                <h2 className="text-base font-semibold text-white">Added to release</h2>
                            </div>
                        ) : result.cherryPickStatus === 'pr-raised' ? (
                            <div className="flex items-center gap-3">
                                <span className="text-blue-400 text-xl">⟳</span>
                                <h2 className="text-base font-semibold text-white">PR raised — CI running</h2>
                            </div>
                        ) : result.cherryPickStatus === 'conflict' ? (
                            <div className="flex items-center gap-3">
                                <span className="text-amber-400 text-xl">⚠</span>
                                <h2 className="text-base font-semibold text-white">Cherry-pick conflict</h2>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3">
                                <span className="text-red-400 text-xl">✗</span>
                                <h2 className="text-base font-semibold text-white">Failed — check API logs</h2>
                            </div>
                        )}
                        <div className="rounded-lg bg-slate-900/60 border border-slate-700 p-3 flex flex-col gap-2">
                            <p className="text-xs text-slate-400">
                                {result.branchCreated ? 'Release branch created' : 'Release branch'}
                            </p>
                            <p className="text-sm font-mono text-emerald-300">{result.releaseBranch}</p>
                        </div>
                        {result.cherryPickStatus === 'already-merged' ? (
                            <p className="text-xs text-slate-400">
                                AB#{item.id} is closed and tagged <strong className="text-slate-200">in-release</strong>.
                            </p>
                        ) : result.cherryPickStatus === 'pr-raised' ? (
                            <p className="text-xs text-slate-400">
                                A PR has been raised with auto-complete. Once CI passes it will merge
                                automatically and the release will complete — no further action needed.
                                {result.prUrl && <> <a href={result.prUrl} target="_blank" rel="noreferrer" className="text-blue-400 underline">View PR</a></>}
                            </p>
                        ) : result.cherryPickStatus === 'succeeded' ? (
                            <p className="text-xs text-slate-400">
                                Hotfix cherry-picked and auto-PR raised. AB#{item.id} is closed and tagged <strong className="text-slate-200">in-release</strong>.
                            </p>
                        ) : result.cherryPickStatus === 'conflict' ? (
                            <p className="text-xs text-amber-300">
                                The cherry-pick hit a conflict. Resolve it manually on the release branch, then re-try Add to Release or send the item back.
                            </p>
                        ) : (
                            <p className="text-xs text-red-300">
                                Something went wrong. Check the API logs for details. AB#{item.id} has <strong>not</strong> been tagged in-release.
                            </p>
                        )}
                        <div className="flex justify-end">
                            <button
                                onClick={onCancel}
                                className="rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-white hover:bg-slate-600 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div>
                            <h2 className="text-base font-semibold text-white">Add AB#{item.id} to release</h2>
                            <p className="mt-1 text-sm text-slate-400 line-clamp-2">{item.title}</p>
                        </div>

                        <div className="rounded-lg bg-slate-900/60 border border-slate-700 p-3 flex flex-col gap-1.5">
                            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Release branch</p>
                            <p className="text-sm font-mono text-slate-200">{releaseBranch}</p>
                            <p className="text-xs text-slate-500">Created from main HEAD if it doesn't already exist</p>
                        </div>

                        <p className="text-xs text-slate-400">
                            The work item will be tagged <strong className="text-slate-200">in-release</strong>.
                        </p>

                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={onCancel}
                                disabled={isPending}
                                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onConfirm}
                                disabled={isPending}
                                className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600 transition-colors disabled:opacity-50"
                            >
                                {isPending ? 'Adding…' : 'Add to Release'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

// ── Sprint Picker Dropdown (portal) ──────────────────────────────────────────

function SprintPickerDropdown({ sprintNumbers, anchorRef, onPick, onClose, isPending, nextSprintNumber, onPlanNew }: {
    sprintNumbers: number[]
    anchorRef: React.RefObject<HTMLButtonElement | null>
    onPick: (n: number) => void
    onClose: () => void
    isPending: boolean
    nextSprintNumber?: number
    onPlanNew?: (n: number) => void
}) {
    const [pos, setPos] = useState<{ top: number; left: number; anchorTop: number } | null>(null)
    const ref = useRef<HTMLDivElement>(null)

    useEscapeKey(onClose)

    useLayoutEffect(() => {
        if (anchorRef.current) {
            const r = anchorRef.current.getBoundingClientRect()
            setPos({ top: r.bottom + 4, left: r.left, anchorTop: r.top })
        }
    }, [anchorRef])

    useLayoutEffect(() => {
        if (!pos || !ref.current) return
        const dropH = ref.current.getBoundingClientRect().height
        if (pos.top + dropH > window.innerHeight - 8) {
            setPos(p => p ? { ...p, top: p.anchorTop - dropH - 4 } : p)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pos?.top])

    useEffect(() => {
        function handler(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) { onClose() }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [onClose])

    if (!pos) return null

    return createPortal(
        <div
            ref={ref}
            onClick={e => e.stopPropagation()}
            style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
            className="min-w-[160px] rounded-xl border border-slate-700 bg-slate-800 shadow-2xl py-1"
        >
            {sprintNumbers.length > 0 && (
                <>
                    <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Add to sprint</p>
                    {sprintNumbers.map(n => (
                        <button
                            key={n}
                            onClick={e => { e.stopPropagation(); onPick(n) }}
                            disabled={isPending}
                            className="w-full text-left px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700 transition-colors disabled:opacity-50"
                        >
                            Sprint {n}
                        </button>
                    ))}
                </>
            )}
            {nextSprintNumber !== undefined && onPlanNew && (
                <>
                    {sprintNumbers.length > 0 && <div className="my-1 border-t border-slate-700" />}
                    <button
                        onClick={e => { e.stopPropagation(); onPlanNew(nextSprintNumber) }}
                        disabled={isPending}
                        className="w-full text-left px-3 py-1.5 text-sm text-indigo-400 hover:bg-slate-700 transition-colors disabled:opacity-50"
                    >
                        + Plan Sprint {nextSprintNumber}
                    </button>
                </>
            )}
        </div>,
        document.body
    )
}

// ── Work Item Row ─────────────────────────────────────────────────────────────

interface WorkItemRowProps {
    item: WorkItemSummary
    tab: WorkItemTabId
    visible: boolean
    delay: number
    selected?: boolean
    onSelect?: (id: number, checked: boolean) => void
    availableSprintNumbers?: number[]
}

function WorkItemRow({ item, tab, visible, delay, selected, onSelect, availableSprintNumbers }: WorkItemRowProps) {
    const queryClient = useQueryClient()
    const [showModal,          setShowModal]          = useState<'start' | 'release' | null>(null)
    const [startResult,        setStartResult]        = useState<StartWorkResult | null>(null)
    const [addResult,          setAddResult]          = useState<AddToReleaseResult | null>(null)
    const sprintPickerAnchorRef = useRef<HTMLButtonElement>(null)
    const [titleExpanded,      setTitleExpanded]      = useState(false)
    const [showAssignPicker,       setShowAssignPicker]       = useState(false)
    const [showBacklogAssignPicker, setShowBacklogAssignPicker] = useState(false)
    const [showClosePrompt,    setShowClosePrompt]    = useState(false)
    const [closeReason,        setCloseReason]        = useState('')
    const openModal = useContext(WorkItemModalContext)

    const isSpecNeeded     = tab === 'parking-lot' && hasTag(item.tags, 'spec-needed')
    const isSpecInProgress = tab === 'parking-lot' && hasTag(item.tags, 'spec-in-progress')

    const moduleTags = (tab === 'backlog' || tab === 'parking-lot')
        ? (item.tags ?? '').split(';').map(t => t.trim()).filter(t => t.length > 0 && !/^Sprint\s*\d/i.test(t))
        : []

    const takeSpecMutation = useMutation({
        mutationFn: () => releaseApi.takeSpec(item.id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['releases-parking-lot'] })
        },
    })

    const assignSpecMutation = useMutation({
        mutationFn: (assignTo: string) => releaseApi.assignSpec(item.id, { assignTo }),
        onSuccess: () => {
            setShowAssignPicker(false)
            void queryClient.invalidateQueries({ queryKey: ['releases-parking-lot'] })
        },
    })

    const promoteMutation = useMutation({
        mutationFn: () => releaseApi.promoteToBacklog(item.id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['releases-parking-lot'] })
            void queryClient.invalidateQueries({ queryKey: ['releases-backlog'] })
        },
    })

    const parkMutation = useMutation({
        mutationFn: () => releaseApi.sendToParkingLot(item.id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['releases-backlog'] })
            void queryClient.invalidateQueries({ queryKey: ['releases-parking-lot'] })
        },
    })

    const [showSprintPicker, setShowSprintPicker] = useState(false)

    const addToSprintMutation = useMutation({
        mutationFn: (sprintNumber: number) => releaseApi.addItemsToSprint(sprintNumber, { workItemIds: [item.id] }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['releases-backlog'] })
            void queryClient.invalidateQueries({ queryKey: ['sprint-active'] })
            setShowSprintPicker(false)
        },
    })

    const planNewSprintMutation = useMutation({
        mutationFn: (sprintNumber: number) => releaseApi.planSprint({ sprintNumber, workItemIds: [item.id] }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['releases-backlog'] })
            void queryClient.invalidateQueries({ queryKey: ['sprint-active'] })
            void queryClient.invalidateQueries({ queryKey: ['sprint-available'] })
            void queryClient.invalidateQueries({ queryKey: ['sprint-next-number'] })
            setShowSprintPicker(false)
        },
    })

    const assignBacklogMutation = useMutation({
        mutationFn: (assignTo: string) => releaseApi.assignBacklogItem(item.id, { assignTo }),
        onSuccess: () => {
            setShowBacklogAssignPicker(false)
            void queryClient.invalidateQueries({ queryKey: ['releases-backlog'] })
        },
    })

    // Team members — only loaded when the picker is opened.
    const { data: teamData } = useQuery({
        queryKey: ['team-members'],
        queryFn: releaseApi.getTeamMembers,
        staleTime: 5 * 60_000,
        enabled: showAssignPicker || showBacklogAssignPicker,
    })

    const startMutation = useMutation({
        mutationFn: () => releaseApi.startWork({ workItemId: item.id, workItemType: item.workItemType }),
        onSuccess: (result) => {
            setStartResult(result)
            void queryClient.invalidateQueries({ queryKey: ['releases-backlog'] })
            void queryClient.invalidateQueries({ queryKey: ['releases-in-progress'] })
        },
    })

    const addMutation = useMutation({
        mutationFn: () => releaseApi.addToRelease({ workItemId: item.id, workItemType: item.workItemType }),
        onSuccess: (result) => {
            setAddResult(result)
            // Invalidation is deferred to handleModalClose so the modal stays visible
            // until the user reads the result — invalidating here unmounts the card.
        },
        onError: () => {
            setAddResult({ workItemId: item.id, releaseBranch: '', branchCreated: false, lastCommitSha: null, cherryPickStatus: 'failed', prUrl: null })
        },
    })

    const markReleasedMutation = useMutation({
        mutationFn: () => releaseApi.markReleased(item.id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['releases-ready'] })
        },
    })

    const closeMutation = useMutation({
        mutationFn: (reason: string) => releaseApi.closeWorkItem(item.id, reason || undefined),
        onSuccess: () => {
            setShowClosePrompt(false)
            setCloseReason('')
            void queryClient.invalidateQueries({ queryKey: ['releases-backlog'] })
            void queryClient.invalidateQueries({ queryKey: ['releases-parking-lot'] })
        },
    })

    function handleModalClose() {
        if (addResult !== null) {
            void queryClient.invalidateQueries({ queryKey: ['releases-ready'] })
            void queryClient.invalidateQueries({ queryKey: ['releases-active'] })
        }
        setShowModal(null)
        setStartResult(null)
        setAddResult(null)
        startMutation.reset()
        addMutation.reset()
    }

    const branchName = getBranchName(item)
    const activeError = startMutation.isError || addMutation.isError
    const activeErrorMsg = startMutation.error ?? addMutation.error

    return (
        <>
            <div
                className={[
                    'group flex items-start gap-3 px-5 py-3.5 transition-all duration-300 cursor-pointer',
                    selected ? 'bg-indigo-950/30' : 'hover:bg-slate-700/20',
                ].join(' ')}
                style={{ opacity: visible ? 1 : 0, transitionDelay: `${delay}ms` }}
                onClick={() => openModal(item.id)}
            >
                {/* Bulk-select checkbox (backlog only) */}
                {onSelect && (
                    <span className="shrink-0 pt-0.5 flex items-center">
                        <input
                            type="checkbox"
                            checked={selected ?? false}
                            onChange={e => onSelect(item.id, e.target.checked)}
                            onClick={e => e.stopPropagation()}
                            className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500/30 cursor-pointer"
                        />
                    </span>
                )}

                {/* ID */}
                <span className="text-xs font-mono text-slate-500 w-14 shrink-0 pt-0.5">#{item.id}</span>

                {/* Type badge */}
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold shrink-0 w-24 justify-center mt-0.5 ${typeStyle(item.workItemType)}`}>
                    {item.workItemType === 'User Story' ? 'Story' : item.workItemType}
                </span>

                {/* Priority */}
                <span className="w-6 shrink-0 text-center pt-0.5">{priorityLabel(item.priority)}</span>


                {/* Title — click to expand full text */}
                <div
                    className="flex-1 min-w-0 flex items-start gap-2 cursor-pointer"
                    onClick={e => { e.stopPropagation(); setTitleExpanded(ex => !ex) }}
                    title={titleExpanded ? undefined : item.title}
                >
                    <span className="flex-1 min-w-0">
                        {titleExpanded ? (
                            <span className="text-sm text-slate-200 break-words whitespace-normal leading-snug">{item.title}</span>
                        ) : (
                            <span className="text-sm text-slate-200 truncate block">{item.title}</span>
                        )}
                    </span>
                    {isSpecInProgress && <SpecBadge />}
                </div>

                {/* Module tag chips (backlog / parking-lot) */}
                {moduleTags.length > 0 && (
                    <span className="hidden sm:flex items-center gap-1 shrink-0">
                        {moduleTags.slice(0, 3).map(tag => (
                            <span key={tag} className="rounded px-1.5 py-0.5 text-[9px] font-medium bg-slate-700/60 text-slate-400 border border-slate-600/50">
                                {tag}
                            </span>
                        ))}
                    </span>
                )}

                {/* Branch (in-progress / ready) */}
                {tab !== 'backlog' && tab !== 'parking-lot' && (
                    <span className="hidden lg:block text-xs font-mono text-slate-500 shrink-0 truncate w-44 pt-0.5">
                        {branchName}
                    </span>
                )}

                {/* Assigned + age */}
                <span className="text-xs text-slate-500 shrink-0 hidden sm:block w-20 truncate pt-0.5">
                    {item.assignedTo ? item.assignedTo.split(' ')[0] : '—'}
                </span>
                <span className="text-xs text-slate-600 shrink-0 w-14 text-right pt-0.5">
                    {relativeAge(item.createdDate)}
                </span>

                {/* Action button */}
                {/* Parking-lot: Take spec or assign to someone */}
                {tab === 'parking-lot' && isSpecNeeded && (
                    <span className={`relative ml-1 flex items-center gap-1 shrink-0 transition-opacity ${showAssignPicker ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                        <button
                            onClick={e => { e.stopPropagation(); takeSpecMutation.mutate() }}
                            disabled={takeSpecMutation.isPending}
                            className="opacity-0 group-hover:opacity-100 transition-opacity rounded-full bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-500 whitespace-nowrap disabled:opacity-50"
                        >
                            {takeSpecMutation.isPending ? 'Taking…' : 'Take spec ▶'}
                        </button>
                        <button
                            onClick={e => { e.stopPropagation(); setShowAssignPicker(v => !v) }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity rounded-full bg-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-600 whitespace-nowrap"
                            title="Assign to someone"
                        >
                            ↓
                        </button>
                        {showAssignPicker && (
                            <div onClick={e => e.stopPropagation()} className="absolute right-0 top-7 z-50 min-w-[180px] rounded-xl border border-slate-700 bg-slate-800 shadow-2xl py-1">
                                <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Assign to</p>
                                {(teamData?.members ?? []).map(m => (
                                    <button
                                        key={m.uniqueName}
                                        onClick={e => { e.stopPropagation(); assignSpecMutation.mutate(m.uniqueName) }}
                                        disabled={assignSpecMutation.isPending}
                                        className="w-full text-left px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700 transition-colors disabled:opacity-50"
                                    >
                                        {m.displayName}
                                    </button>
                                ))}
                                {(teamData?.members ?? []).length === 0 && (
                                    <p className="px-3 py-1.5 text-sm text-slate-500">No members found</p>
                                )}
                            </div>
                        )}
                    </span>
                )}
                {tab === 'parking-lot' && isSpecInProgress && (
                    <button
                        onClick={e => { e.stopPropagation(); promoteMutation.mutate() }}
                        disabled={promoteMutation.isPending}
                        className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity rounded-full bg-emerald-700 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-600 shrink-0 whitespace-nowrap disabled:opacity-50"
                        title="Spec is complete — move to Backlog for Start Work"
                    >
                        {promoteMutation.isPending ? 'Promoting…' : 'Spec complete →'}
                    </button>
                )}
                {tab === 'parking-lot' && !isSpecNeeded && !isSpecInProgress && (
                    <>
                        <button
                            onClick={e => { e.stopPropagation(); promoteMutation.mutate() }}
                            disabled={promoteMutation.isPending}
                            title={promoteMutation.isError ? String((promoteMutation.error as Error | null)?.message ?? 'Failed') : undefined}
                            className={`ml-1 opacity-0 group-hover:opacity-100 transition-opacity rounded-full px-3 py-1 text-xs font-semibold shrink-0 whitespace-nowrap disabled:opacity-50 ${promoteMutation.isError ? 'bg-red-900/40 text-red-400' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
                        >
                            {promoteMutation.isPending ? 'Moving…' : promoteMutation.isError ? '! Retry' : '→ Backlog'}
                        </button>
                        {!showClosePrompt && (
                            <button
                                onClick={e => { e.stopPropagation(); setShowClosePrompt(true) }}
                                className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity rounded-full bg-slate-700 px-3 py-1 text-xs font-semibold text-slate-400 hover:bg-red-900/40 hover:text-red-400 shrink-0 whitespace-nowrap"
                            >
                                Close
                            </button>
                        )}
                        {showClosePrompt && (
                            <span className="ml-1 flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                                <input
                                    autoFocus
                                    type="text"
                                    value={closeReason}
                                    onChange={e => setCloseReason(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') { closeMutation.mutate(closeReason) }
                                        if (e.key === 'Escape') { setShowClosePrompt(false); setCloseReason('') }
                                    }}
                                    placeholder="Reason (optional)"
                                    className="rounded-lg border border-slate-600 bg-slate-900 px-2 py-0.5 text-xs text-slate-200 placeholder-slate-500 focus:border-red-500 focus:outline-none w-40"
                                    disabled={closeMutation.isPending}
                                />
                                <button
                                    onClick={() => closeMutation.mutate(closeReason)}
                                    disabled={closeMutation.isPending}
                                    className="rounded-full bg-red-800 px-2 py-0.5 text-xs font-semibold text-red-200 hover:bg-red-700 disabled:opacity-50"
                                >
                                    {closeMutation.isPending ? '…' : 'Confirm'}
                                </button>
                                <button
                                    onClick={() => { setShowClosePrompt(false); setCloseReason('') }}
                                    className="rounded-full bg-slate-700 px-2 py-0.5 text-xs text-slate-400 hover:bg-slate-600"
                                >
                                    Cancel
                                </button>
                            </span>
                        )}
                    </>
                )}

                {tab === 'backlog' && (
                    <span className={`relative ml-1 flex items-center gap-1 shrink-0 transition-opacity ${showSprintPicker || showBacklogAssignPicker ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                        <button
                            onClick={e => { e.stopPropagation(); parkMutation.mutate() }}
                            disabled={parkMutation.isPending}
                            className="rounded-full bg-slate-700 px-3 py-1 text-xs font-semibold text-slate-300 hover:bg-slate-600 whitespace-nowrap disabled:opacity-50"
                        >
                            {parkMutation.isPending ? 'Parking…' : '← Park'}
                        </button>
                        {availableSprintNumbers !== undefined && (() => {
                            const nextN = availableSprintNumbers.length > 0
                                ? Math.max(...availableSprintNumbers) + 1
                                : 1
                            const anyPending = addToSprintMutation.isPending || planNewSprintMutation.isPending
                            return (
                                <>
                                    <button
                                        ref={sprintPickerAnchorRef}
                                        onClick={e => { e.stopPropagation(); setShowSprintPicker(v => !v) }}
                                        disabled={anyPending}
                                        className="rounded-full bg-indigo-700 px-3 py-1 text-xs font-semibold text-white hover:bg-indigo-600 whitespace-nowrap disabled:opacity-50"
                                    >
                                        {anyPending ? 'Working…' : availableSprintNumbers.length === 1 ? `→ Sprint ${availableSprintNumbers[0]!} ▾` : '→ Sprint ▾'}
                                    </button>
                                    {showSprintPicker && (
                                        <SprintPickerDropdown
                                            sprintNumbers={availableSprintNumbers}
                                            anchorRef={sprintPickerAnchorRef}
                                            onPick={n => addToSprintMutation.mutate(n)}
                                            onClose={() => setShowSprintPicker(false)}
                                            isPending={anyPending}
                                            nextSprintNumber={nextN}
                                            onPlanNew={n => planNewSprintMutation.mutate(n)}
                                        />
                                    )}
                                </>
                            )
                        })()}
                        <button
                            onClick={e => { e.stopPropagation(); setShowBacklogAssignPicker(v => !v) }}
                            className="rounded-full bg-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-600 whitespace-nowrap"
                            title="Assign to someone"
                        >
                            ↓
                        </button>
                        {showBacklogAssignPicker && (
                            <div onClick={e => e.stopPropagation()} className="absolute right-0 top-7 z-50 min-w-[180px] rounded-xl border border-slate-700 bg-slate-800 shadow-2xl py-1">
                                <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Assign to</p>
                                {(teamData?.members ?? []).map(m => (
                                    <button
                                        key={m.uniqueName}
                                        onClick={e => { e.stopPropagation(); assignBacklogMutation.mutate(m.uniqueName) }}
                                        disabled={assignBacklogMutation.isPending}
                                        className="w-full text-left px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700 transition-colors disabled:opacity-50"
                                    >
                                        {m.displayName}
                                    </button>
                                ))}
                                {(teamData?.members ?? []).length === 0 && (
                                    <p className="px-3 py-1.5 text-sm text-slate-500">No members found</p>
                                )}
                            </div>
                        )}
                        {!showClosePrompt && (
                            <button
                                onClick={e => { e.stopPropagation(); setShowClosePrompt(true) }}
                                className="rounded-full bg-slate-700 px-2 py-1 text-xs text-slate-400 hover:bg-red-900/40 hover:text-red-400 whitespace-nowrap"
                                title="Close this work item"
                            >
                                ✕
                            </button>
                        )}
                        {showClosePrompt && (
                            <span className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                <input
                                    autoFocus
                                    type="text"
                                    value={closeReason}
                                    onChange={e => setCloseReason(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') { closeMutation.mutate(closeReason) }
                                        if (e.key === 'Escape') { setShowClosePrompt(false); setCloseReason('') }
                                    }}
                                    placeholder="Reason (optional)"
                                    className="rounded-lg border border-slate-600 bg-slate-900 px-2 py-0.5 text-xs text-slate-200 placeholder-slate-500 focus:border-red-500 focus:outline-none w-36"
                                    disabled={closeMutation.isPending}
                                />
                                <button
                                    onClick={() => closeMutation.mutate(closeReason)}
                                    disabled={closeMutation.isPending}
                                    className="rounded-full bg-red-800 px-2 py-0.5 text-xs font-semibold text-red-200 hover:bg-red-700 disabled:opacity-50"
                                >
                                    {closeMutation.isPending ? '…' : 'Close'}
                                </button>
                                <button
                                    onClick={() => { setShowClosePrompt(false); setCloseReason('') }}
                                    className="rounded-full bg-slate-700 px-2 py-0.5 text-xs text-slate-400 hover:bg-slate-600"
                                >
                                    Cancel
                                </button>
                            </span>
                        )}
                    </span>
                )}

                {tab === 'ready' && (
                    <span className="ml-1 flex items-center gap-1.5 shrink-0">
                        <button
                            onClick={e => { e.stopPropagation(); setShowModal('release') }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity rounded-full bg-emerald-700 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-600 whitespace-nowrap"
                        >
                            Add to Release ▶
                        </button>
                        <button
                            onClick={e => { e.stopPropagation(); markReleasedMutation.mutate() }}
                            disabled={markReleasedMutation.isPending}
                            title="Already merged to main directly — mark as released without cherry-pick"
                            className="opacity-0 group-hover:opacity-100 transition-opacity rounded-full bg-slate-600 px-3 py-1 text-xs font-semibold text-slate-200 hover:bg-slate-500 whitespace-nowrap disabled:opacity-50"
                        >
                            {markReleasedMutation.isPending ? '…' : 'Mark Released'}
                        </button>
                    </span>
                )}
            </div>

            {showModal === 'start' && (
                <StartWorkModal
                    item={item}
                    onConfirm={() => startMutation.mutate()}
                    onCancel={handleModalClose}
                    isPending={startMutation.isPending}
                    result={startResult}
                />
            )}

            {showModal === 'release' && (
                <AddToReleaseModal
                    item={item}
                    onConfirm={() => addMutation.mutate()}
                    onCancel={handleModalClose}
                    isPending={addMutation.isPending}
                    result={addResult}
                />
            )}

            {activeError && showModal === null && (
                <div className="mx-5 mb-2 px-3 py-1.5 rounded-lg bg-red-950/50 border border-red-800/40 text-xs text-red-300">
                    {activeErrorMsg instanceof Error ? activeErrorMsg.message : 'Action failed'}
                </div>
            )}
        </>
    )
}

// ── Tab Panel (backlog / in-progress / ready) ─────────────────────────────────

type SortCol = 'id' | 'priority' | 'type' | 'title' | 'age'
type SortDir = 'asc' | 'desc'

interface TabPanelProps {
    tab: WorkItemTabId
}

function SortHeader({
    col, label, active, dir, width, onSort, right,
}: {
    col: SortCol; label: string; active: boolean; dir: SortDir
    width: string; onSort: (c: SortCol) => void; right?: boolean
}) {
    return (
        <button
            onClick={() => onSort(col)}
            className={[
                'text-[10px] font-semibold uppercase tracking-widest shrink-0 flex items-center gap-0.5 hover:text-slate-300 transition-colors',
                right ? 'justify-end' : '',
                width,
                active ? 'text-slate-300' : 'text-slate-500',
            ].join(' ')}
        >
            {label}
            {active && <span className="ml-0.5">{dir === 'asc' ? '↑' : '↓'}</span>}
        </button>
    )
}

// ── Iteration path grouping ───────────────────────────────────────────────────

function sprintLabel(iterationPath: string): string {
    // "Marque\Sprint W" → "Sprint W"; fall back to last segment
    const parts = iterationPath.split('\\')
    return parts[parts.length - 1] ?? iterationPath
}

interface IterationGroupProps {
    iterationPath: string | null
    items: WorkItemSummary[]
    tab: WorkItemTabId
    visible: boolean
    startIndex: number
    selectedIds?: Set<number>
    onSelect?: (id: number, checked: boolean) => void
    availableSprintNumbers?: number[]
}

function IterationGroup({ iterationPath, items, tab, visible, startIndex, selectedIds, onSelect, availableSprintNumbers }: IterationGroupProps) {
    const [collapsed, setCollapsed] = useState(false)
    const isUngrouped = iterationPath === null
    const label = iterationPath ? sprintLabel(iterationPath) : 'No Sprint'

    return (
        <div>
            <div className="flex items-center gap-2 px-5 py-2 bg-slate-900/50 border-b border-slate-700/60">
                <button
                    onClick={() => setCollapsed(c => !c)}
                    className="flex items-center gap-2 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                >
                    <svg
                        className={`h-3 w-3 text-slate-500 transition-transform shrink-0 ${collapsed ? '-rotate-90' : ''}`}
                        viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round"
                    >
                        <path d="M4 6l4 4 4-4" />
                    </svg>
                    {!isUngrouped && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-500/70 shrink-0">Sprint</span>
                    )}
                    <span className={`text-xs font-medium truncate ${isUngrouped ? 'text-slate-500 italic' : 'text-indigo-300'}`}>
                        {label}
                    </span>
                    <span className="ml-1 text-[10px] text-slate-600 bg-slate-700/50 rounded-full px-1.5 py-0.5 shrink-0">
                        {items.length}
                    </span>
                </button>
            </div>

            {!collapsed && (
                <div className="divide-y divide-slate-700/30">
                    {items.map((item, i) => (
                        <WorkItemRow
                            key={item.id}
                            item={item}
                            tab={tab}
                            visible={visible}
                            delay={(startIndex + i) * 40}
                            {...(availableSprintNumbers !== undefined ? { availableSprintNumbers } : {})}
                            {...(onSelect ? { selected: selectedIds?.has(item.id) ?? false, onSelect } : {})}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

function GroupedRows({
    items, tab, visible, selectedIds, onSelect, availableSprintNumbers,
}: {
    items: WorkItemSummary[]
    tab: WorkItemTabId
    visible: boolean
    selectedIds?: Set<number>
    onSelect?: (id: number, checked: boolean) => void
    availableSprintNumbers?: number[]
}) {
    const hasIterations = items.some(i => i.iterationPath !== null)

    if (!hasIterations) {
        return (
            <div className="divide-y divide-slate-700/30">
                {items.map((item, i) => (
                    <WorkItemRow
                        key={item.id} item={item} tab={tab} visible={visible} delay={i * 40}
                        {...(availableSprintNumbers !== undefined ? { availableSprintNumbers } : {})}
                        {...(onSelect ? { selected: selectedIds?.has(item.id) ?? false, onSelect } : {})}
                    />
                ))}
            </div>
        )
    }

    const iterMap = new Map<string, WorkItemSummary[]>()
    const ungrouped: WorkItemSummary[] = []

    for (const item of items) {
        if (item.iterationPath === null) {
            ungrouped.push(item)
        } else {
            if (!iterMap.has(item.iterationPath)) {
                iterMap.set(item.iterationPath, [])
            }
            iterMap.get(item.iterationPath)!.push(item)
        }
    }

    // Sort by sprint label descending (newest sprint first)
    const iterGroups = [...iterMap.entries()].sort((a, b) => b[0].localeCompare(a[0]))
    const selectionProps = onSelect && selectedIds ? { selectedIds, onSelect } : {}

    let idx = 0
    const groupEls = iterGroups.map(([path, groupItems]) => {
        const el = (
            <IterationGroup
                key={path}
                iterationPath={path}
                items={groupItems}
                tab={tab}
                visible={visible}
                startIndex={idx}
                {...(availableSprintNumbers !== undefined ? { availableSprintNumbers } : {})}
                {...selectionProps}
            />
        )
        idx += groupItems.length
        return el
    })

    const ungroupedEl = ungrouped.length > 0 ? (
        <IterationGroup
            key="ungrouped"
            iterationPath={null}
            items={ungrouped}
            tab={tab}
            visible={visible}
            startIndex={idx}
            {...(availableSprintNumbers !== undefined ? { availableSprintNumbers } : {})}
            {...selectionProps}
        />
    ) : null

    return <>{groupEls}{ungroupedEl}</>
}

// ── Tab Panel ─────────────────────────────────────────────────────────────────

function TabPanel({ tab }: TabPanelProps) {
    const [visible,      setVisible]      = useState(false)
    const [sortCol,      setSortCol]      = useState<SortCol>('id')
    const [sortDir,      setSortDir]      = useState<SortDir>('asc')
    const [areaFilter,   setAreaFilter]   = useState<Set<string>>(new Set())
    const [selectedIds,  setSelectedIds]  = useState<Set<number>>(new Set())
    const [search,       setSearch]       = useState('')

    const { data: availableSprintsData } = useQuery({
        queryKey: ['sprint-available'],
        queryFn: releaseApi.getAvailableSprints,
        staleTime: 60_000,
        enabled: tab === 'backlog',
    })
    const availableSprintNumbers = availableSprintsData?.sprintNumbers

    const queryMap = {
        'parking-lot': { key: ['releases-parking-lot'], fn: releaseApi.getReleasesParkingLot },
        'backlog':     { key: ['releases-backlog'],     fn: releaseApi.getReleasesBacklog },
        'in-progress': { key: ['releases-in-progress'], fn: releaseApi.getReleasesInProgress },
        'ready':       { key: ['releases-ready'],       fn: releaseApi.getReleasesReady },
    }
    const { key, fn } = queryMap[tab]

    const { data, isLoading, isError } = useQuery({
        queryKey: key,
        queryFn: fn,
        staleTime: 60_000,
    })

    useEffect(() => {
        if (data) {
            setVisible(false)
            const t = setTimeout(() => setVisible(true), 60)
            return () => clearTimeout(t)
        }
        return undefined
    }, [data])

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <span className="text-slate-500 text-sm animate-pulse">Loading…</span>
            </div>
        )
    }

    if (isError) {
        return (
            <div className="rounded-xl border border-red-800 bg-red-950/50 p-5 text-red-300 text-sm">
                Failed to load work items.
            </div>
        )
    }

    // Backlog and parking-lot: hide items that have been assigned to a sprint (they live in the Sprint tab).
    // In-progress and ready: sprint items that have progressed belong here — do not filter them out.
    const sprintPathPattern = SPRINT_PATH_PATTERN
    const allItems = (tab === 'backlog' || tab === 'parking-lot')
        ? (data?.items ?? []).filter(item => !item.iterationPath || !sprintPathPattern.test(item.iterationPath))
        : (data?.items ?? [])

    if (allItems.length === 0) {
        return (
            <div className="py-16 text-center text-slate-500 text-sm">
                {tab === 'parking-lot' && 'Nothing parked — all items are sprint-ready.'}
                {tab === 'backlog'     && 'No backlog items — all are assigned to a sprint or in progress.'}
                {tab === 'in-progress' && 'Nothing in progress right now.'}
                {tab === 'ready'       && 'Nothing waiting for release.'}
            </div>
        )
    }

    // Derive all distinct tags across items for filter chips (excludes sprint labels)
    const SKIP_TAG = /^(parking-lot|spec-needed|spec-in-progress|in-release|in-pr)$/i
    const allAreaLabels = Array.from(
        new Set(allItems.flatMap(item =>
            (item.tags ?? '').split(';').map(t => t.trim()).filter(t => t.length > 0 && !SKIP_TAG.test(t))
        ))
    ).sort()

    function getItemTags(tags: string | null): Set<string> {
        return new Set((tags ?? '').split(';').map(t => t.trim()).filter(t => t.length > 0))
    }

    // Filter by tag
    const areaFiltered = areaFilter.size === 0
        ? allItems
        : allItems.filter(item => {
            const tags = getItemTags(item.tags)
            return [...areaFilter].some(a => tags.has(a))
        })

    // Filter by search (title text or ticket number)
    const q = search.trim().toLowerCase()
    const filtered = q
        ? areaFiltered.filter(i => i.title.toLowerCase().includes(q) || String(i.id).includes(q))
        : areaFiltered

    // Sort
    const sorted = [...filtered].sort((a, b) => {
        let cmp = 0
        if (sortCol === 'id')       cmp = a.id - b.id
        if (sortCol === 'priority') cmp = (a.priority ?? 99) - (b.priority ?? 99)
        if (sortCol === 'type')     cmp = a.workItemType.localeCompare(b.workItemType)
        if (sortCol === 'title')    cmp = a.title.localeCompare(b.title)
        if (sortCol === 'age')      cmp = (a.createdDate ?? '').localeCompare(b.createdDate ?? '')
        return sortDir === 'asc' ? cmp : -cmp
    })

    function toggleSort(col: SortCol) {
        if (col === sortCol) { setSortDir(d => d === 'asc' ? 'desc' : 'asc') }
        else { setSortCol(col); setSortDir('asc') }
    }

    function toggleArea(label: string) {
        setAreaFilter(prev => {
            const next = new Set(prev)
            if (next.has(label)) { next.delete(label) } else { next.add(label) }
            return next
        })
    }

    function toggleSelect(id: number, checked: boolean) {
        setSelectedIds(prev => {
            const next = new Set(prev)
            if (checked) { next.add(id) } else { next.delete(id) }
            return next
        })
    }

    function toggleSelectAll() {
        if (selectedIds.size === sorted.length) {
            setSelectedIds(new Set())
        } else {
            setSelectedIds(new Set(sorted.map(i => i.id)))
        }
    }

    const isBacklog   = tab === 'backlog'
    const allSelected = isBacklog && sorted.length > 0 && selectedIds.size === sorted.length
    const someSelected = isBacklog && selectedIds.size > 0

    return (
        <div className="rounded-xl border border-slate-700 bg-slate-800/40 overflow-hidden">

            {/* Filter bar: tag chips + search */}
            <div className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-b border-slate-700/60 bg-slate-900/40">
                {allAreaLabels.length > 0 && (
                    <>
                        <span className="text-[10px] text-slate-600 uppercase tracking-wider shrink-0">Tags</span>
                        {allAreaLabels.map(label => {
                            const active = areaFilter.has(label)
                            const style = [...Object.values(AREA_BADGE), ...Object.values(MODULE_BADGE)]
                                .find(b => b.label === label)?.style ?? 'bg-slate-500/20 text-slate-400 ring-1 ring-slate-500/30'
                            return (
                                <button
                                    key={label}
                                    onClick={() => toggleArea(label)}
                                    className={[
                                        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-all',
                                        active ? style : 'bg-slate-800 text-slate-500 ring-1 ring-slate-700 hover:text-slate-300',
                                    ].join(' ')}
                                >
                                    {label}
                                </button>
                            )
                        })}
                        {areaFilter.size > 0 && (
                            <button
                                onClick={() => setAreaFilter(new Set())}
                                className="text-[10px] text-slate-600 hover:text-slate-400 transition-colors ml-1"
                            >
                                clear
                            </button>
                        )}
                    </>
                )}
                <input
                    type="text"
                    placeholder="Search # or title…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="bg-slate-800 border border-slate-600/60 rounded-lg px-2.5 py-1 text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-indigo-500 w-44"
                />
                <span className="ml-auto text-[10px] text-slate-600">
                    {filtered.length !== allItems.length && `${filtered.length} of `}{allItems.length} item{allItems.length !== 1 ? 's' : ''}
                </span>
            </div>

            {/* Column headers */}
            <div className="flex items-center gap-3 px-5 py-2.5 border-b border-slate-700/60 bg-slate-800/60">
                {/* Select-all checkbox (backlog only) */}
                {isBacklog && (
                    <span className="shrink-0 flex items-center">
                        <input
                            type="checkbox"
                            checked={allSelected}
                            ref={el => { if (el) { el.indeterminate = someSelected && !allSelected } }}
                            onChange={toggleSelectAll}
                            className="h-3.5 w-3.5 rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500/30 cursor-pointer"
                            title="Select all"
                        />
                    </span>
                )}
                <SortHeader col="id"       label="ID"    active={sortCol==='id'}       dir={sortDir} width="w-14"   onSort={toggleSort} />
                <SortHeader col="type"     label="Type"  active={sortCol==='type'}     dir={sortDir} width="w-24"   onSort={toggleSort} />
                <SortHeader col="priority" label="P"     active={sortCol==='priority'} dir={sortDir} width="w-6"    onSort={toggleSort} />
                <SortHeader col="title"    label="Title" active={sortCol==='title'}    dir={sortDir} width="flex-1" onSort={toggleSort} />
                {tab !== 'backlog' && tab !== 'parking-lot' && (
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 hidden lg:block w-44 shrink-0">Branch</span>
                )}
                <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 hidden sm:block w-20 shrink-0">Assigned</span>
                <SortHeader col="age"      label="Age"   active={sortCol==='age'}      dir={sortDir} width="w-14"   onSort={toggleSort} right />
                {tab === 'backlog'     && <span className="w-36 shrink-0" />}
                {tab === 'parking-lot' && <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 w-28 shrink-0">Action</span>}
                {tab === 'in-progress' && <span className="w-20 shrink-0" />}
                {tab === 'ready'       && <span className="w-28 shrink-0" />}
            </div>

            {/* Rows */}
            {sorted.length === 0 ? (
                <div className="py-10 text-center text-slate-500 text-sm">No items match the current filter.</div>
            ) : (tab === 'backlog' || tab === 'parking-lot') ? (
                <div className="divide-y divide-slate-700/30">
                    {sorted.map((item, i) => (
                        <WorkItemRow
                            key={item.id} item={item} tab={tab} visible={visible} delay={i * 40}
                            {...(availableSprintNumbers !== undefined ? { availableSprintNumbers } : {})}
                            {...(isBacklog ? { selected: selectedIds.has(item.id), onSelect: toggleSelect } : {})}
                        />
                    ))}
                </div>
            ) : (
                <GroupedRows
                    items={sorted} tab={tab} visible={visible}
                    {...(availableSprintNumbers !== undefined ? { availableSprintNumbers } : {})}
                    {...(isBacklog ? { selectedIds, onSelect: toggleSelect } : {})}
                />
            )}
        </div>
    )
}


// ── History Panel ─────────────────────────────────────────────────────────────

function SprintTestPlanModal({ plan, onClose }: { plan: SprintTestPlan; onClose: () => void }) {
    const mergedDate = new Date(plan.mergedAt).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'long', year: 'numeric',
    })

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto py-10 px-4 print:bg-white print:p-0 print:block print:overflow-visible">
            <div className="w-full max-w-3xl bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl print:shadow-none print:border-0 print:rounded-none print:bg-white print:text-black">
                {/* Header */}
                <div className="px-4 sm:px-8 pt-8 pb-5 border-b border-slate-700 print:border-slate-300 flex items-start justify-between gap-4">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 print:text-slate-600">
                            Test Plan
                        </p>
                        <h2 className="text-xl font-bold text-slate-100 print:text-black mt-1">
                            Sprint {plan.sprintNumber}
                        </h2>
                        <p className="text-sm text-slate-400 print:text-slate-600 mt-0.5">
                            Merged {mergedDate} · PR #{plan.prId} · {plan.items.length} item{plan.items.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 print:hidden">
                        <button
                            onClick={() => window.print()}
                            className="rounded-lg bg-indigo-700 px-4 py-1.5 text-sm font-semibold text-white hover:bg-indigo-600 transition-colors"
                        >
                            Export PDF
                        </button>
                        <button
                            onClick={onClose}
                            className="rounded-lg bg-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-600 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>

                {/* Items */}
                <div className="px-4 sm:px-8 py-6 flex flex-col gap-8">
                    {plan.items.map(item => (
                        <div key={item.id} className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-mono text-slate-500 print:text-slate-600">AB#{item.id}</span>
                                <span className="text-xs text-slate-500 print:text-slate-600">
                                    {item.workItemType}
                                </span>
                                <h3 className="text-sm font-semibold text-slate-200 print:text-black">{item.title}</h3>
                            </div>
                            {item.acceptanceCriteria ? (
                                <div
                                    className="prose prose-sm prose-invert print:prose-slate max-w-none text-slate-300 print:text-black pl-4 border-l-2 border-slate-700 print:border-slate-300"
                                    dangerouslySetInnerHTML={{ __html: item.acceptanceCriteria }}
                                />
                            ) : (
                                <p className="text-xs text-slate-500 italic pl-4">No acceptance criteria recorded.</p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    )
}

function InlinePipelineDots({ pipeline }: { pipeline: SprintReleasePipeline | null | undefined }) {
    const stages = derivePipelineStages(pipeline)
    return (
        <div className="flex items-center gap-0.5">
            {stages.map((stage, i) => {
                const dotClass =
                    stage.status === 'success'  ? 'bg-emerald-500'
                    : stage.status === 'failed'  ? 'bg-red-500'
                    : stage.status === 'running' ? 'bg-amber-400 animate-pulse'
                    : stage.status === 'pending' ? 'bg-slate-500'
                    : 'bg-slate-700'
                const arrowColor =
                    stage.status === 'success' ? '#10b981'
                    : stage.status === 'failed' ? 'rgba(239,68,68,0.4)'
                    : '#334155'
                return (
                    <div key={stage.id} className="flex items-center gap-0.5">
                        <div title={`${stage.label}: ${stage.status}`}
                            className={`w-2 h-2 rounded-full shrink-0 ${dotClass}`}
                        />
                        {i < stages.length - 1 && (
                            <svg width="12" height="6" viewBox="0 0 12 6" fill="none" className="shrink-0">
                                <line x1="0" y1="3" x2="8" y2="3" stroke={arrowColor} strokeWidth="1" />
                                <polygon points="8,1 12,3 8,5" fill={arrowColor} />
                            </svg>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

function SprintHistoryRow({ entry }: { entry: SprintHistorySummary }) {
    const [expanded, setExpanded] = useState(false)
    const [testPlan, setTestPlan] = useState<SprintTestPlan | null>(null)
    const [loadingPlan, setLoadingPlan] = useState(false)
    const [buildQueued, setBuildQueued] = useState(false)
    const queryClient = useQueryClient()
    const mergedDate = new Date(entry.mergedAt).toLocaleDateString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric',
    })

    const stages = derivePipelineStages(entry.pipeline)
    const pipelineFailed = stages.find(s => s.id === 'pipeline')?.status === 'failed'

    const retryMutation = useMutation({
        mutationFn: () => releaseApi.retrySprintPipeline(entry.sprintNumber),
        onSuccess: () => {
            setBuildQueued(true)
            void queryClient.invalidateQueries({ queryKey: ['sprint-history'] })
        },
    })

    const openTestPlan = async (e: React.MouseEvent) => {
        e.stopPropagation()
        if (testPlan) { setTestPlan(testPlan); return }
        setLoadingPlan(true)
        try {
            const plan = await releaseApi.getSprintTestPlan(entry.sprintNumber)
            setTestPlan(plan)
        } finally {
            setLoadingPlan(false)
        }
    }

    return (
        <div>
            {testPlan && <SprintTestPlanModal plan={testPlan} onClose={() => setTestPlan(null)} />}
            <div
                className="flex items-center gap-4 px-5 py-3.5 hover:bg-slate-700/20 transition-colors cursor-pointer select-none"
                onClick={() => setExpanded(v => !v)}
            >
                <span className="text-xs text-slate-500 w-3 shrink-0">{expanded ? '▾' : '▸'}</span>
                <span className="text-sm font-semibold text-slate-200 w-20 shrink-0">
                    Sprint {entry.sprintNumber}
                </span>
                <span className="text-xs text-slate-500 w-28 shrink-0">{mergedDate}</span>
                <span className="text-xs text-slate-500 w-16 shrink-0">
                    {entry.itemCount} item{entry.itemCount !== 1 ? 's' : ''}
                </span>
                <div className="flex-1 flex justify-center">
                    <InlinePipelineDots pipeline={entry.pipeline} />
                </div>
                <div className="w-56 shrink-0 flex items-center justify-end gap-3">
                    {(pipelineFailed || buildQueued) && (
                        <button
                            onClick={e => { e.stopPropagation(); retryMutation.mutate() }}
                            disabled={retryMutation.isPending || buildQueued}
                            className="text-xs text-amber-400 hover:text-amber-300 transition-colors disabled:opacity-50"
                        >
                            {retryMutation.isPending ? 'Queuing…' : buildQueued ? 'Build queued ✓' : '↺ Retry Pipeline'}
                        </button>
                    )}
                    <button
                        onClick={openTestPlan}
                        disabled={loadingPlan}
                        className="text-xs text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50"
                    >
                        {loadingPlan ? 'Loading…' : 'Test Plan'}
                    </button>
                    <a
                        href={entry.prUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-indigo-400 hover:text-indigo-300 font-mono"
                        onClick={e => e.stopPropagation()}
                    >
                        PR #{entry.prId} ↗
                    </a>
                </div>
            </div>
            {expanded && entry.items.length > 0 && (
                <div className="border-t border-slate-700/30 bg-slate-800/30">
                    {entry.items.map(item => (
                        <div key={item.id} className="flex items-center gap-3 px-10 py-2 text-xs text-slate-400">
                            <span className="text-slate-600 font-mono shrink-0">#{item.id}</span>
                            <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] bg-slate-700 text-slate-400">
                                {item.workItemType === 'Bug' ? '🐛' : '◆'} {item.workItemType}
                            </span>
                            <span className="truncate">{item.title}</span>
                            <span className="ml-auto shrink-0 text-slate-600">{item.state}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

function HistoryPanel() {
    const [showOlder, setShowOlder] = useState(false)

    const { data: recentHistory, isLoading: sprintLoading, isError: sprintError } = useQuery({
        queryKey: ['sprint-history', 'recent'],
        queryFn: () => releaseApi.getSprintHistory(10),
        staleTime: 120_000,
    })

    const { data: allHistory, isLoading: olderLoading } = useQuery({
        queryKey: ['sprint-history', 'all'],
        queryFn: () => releaseApi.getSprintHistory(0),
        staleTime: 120_000,
        enabled: showOlder,
    })

    const { data: releaseData } = useQuery({
        queryKey: ['releases-history'],
        queryFn: releaseApi.getReleaseHistory,
        staleTime: 120_000,
    })

    if (sprintLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <span className="text-slate-500 text-sm animate-pulse">Loading…</span>
            </div>
        )
    }

    if (sprintError) {
        return (
            <div className="rounded-xl border border-red-800 bg-red-950/50 p-5 text-red-300 text-sm">
                Failed to load sprint history.
            </div>
        )
    }

    const recentSprints = recentHistory ?? []
    const olderSprints = showOlder && allHistory
        ? allHistory.filter(e => !recentSprints.some(r => r.sprintNumber === e.sprintNumber))
        : []
    const branches = releaseData?.branches ?? []

    return (
        <div className="flex flex-col gap-6">
            {/* Sprint releases */}
            <div>
                <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Sprint releases</h3>
                {recentSprints.length === 0 ? (
                    <div className="py-10 text-center text-slate-500 text-sm rounded-xl border border-slate-700">
                        No sprint releases in the last 90 days.
                    </div>
                ) : (
                    <div className="rounded-xl border border-slate-700 bg-slate-800/40 overflow-hidden divide-y divide-slate-700/30">
                        {recentSprints.map(entry => (
                            <SprintHistoryRow key={entry.sprintNumber} entry={entry} />
                        ))}
                        {showOlder && olderLoading && (
                            <div className="px-5 py-3 text-slate-500 text-sm animate-pulse">Loading older sprints…</div>
                        )}
                        {showOlder && olderSprints.map(entry => (
                            <SprintHistoryRow key={entry.sprintNumber} entry={entry} />
                        ))}
                        <div className="px-5 py-2 flex items-center justify-between border-t border-slate-700/40">
                            <span className="text-[10px] text-slate-600">
                                {recentSprints.length + olderSprints.length} sprint release{recentSprints.length + olderSprints.length !== 1 ? 's' : ''} · last 90 days
                            </span>
                            {!showOlder && (
                                <button
                                    onClick={() => setShowOlder(true)}
                                    className="text-[10px] text-slate-500 hover:text-slate-300 transition-colors"
                                >
                                    Show older →
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Release branches */}
            {branches.length > 0 && (
                <div>
                    <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Release branches</h3>
                    <div className="rounded-xl border border-slate-700 bg-slate-800/40 overflow-hidden divide-y divide-slate-700/30">
                        {branches.map((branch, i) => (
                            <div
                                key={branch.name}
                                className="flex items-center gap-4 px-5 py-3"
                                style={{ transitionDelay: `${i * 30}ms` }}
                            >
                                <span className="text-sm font-semibold text-slate-200 w-28 shrink-0">{branch.dateLabel}</span>
                                <span className="text-xs font-mono text-slate-500 flex-1">{branch.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

// ── Kanban View (#307) ────────────────────────────────────────────────────────

type KanbanColumn = 'parking-lot' | 'backlog' | 'sprint' | 'in-progress' | 'ready'

const SPRINT_PATH_PATTERN = /\\Sprint\s+\d+$/i

function KanbanCard({ item, column, onDragStart, sprintNumber }: {
    item: WorkItemSummary
    column: KanbanColumn
    onDragStart?: (e: React.DragEvent, item: WorkItemSummary, col: KanbanColumn, sprintNumber?: number) => void
    sprintNumber?: number
}) {
    const queryClient = useQueryClient()
    const [feedback, setFeedback] = useState<{ msg: string; ok: boolean } | null>(null)

    const addMutation = useMutation({
        mutationFn: () => releaseApi.addToRelease({ workItemId: item.id, workItemType: item.workItemType }),
        onSuccess: (r) => {
            setFeedback({ msg: `✓ ${r.releaseBranch}`, ok: true })
            void queryClient.invalidateQueries({ queryKey: ['releases-ready'] })
            void queryClient.invalidateQueries({ queryKey: ['releases-active'] })
        },
        onError: (e) => setFeedback({ msg: e instanceof Error ? e.message : 'Failed', ok: false }),
    })

    const isBusy = addMutation.isPending
    const autoBadges = getAreaBadges(item.tags).slice(0, 1)
    const isSprintItem = item.iterationPath != null && SPRINT_PATH_PATTERN.test(item.iterationPath)

    const isDraggable = (column === 'parking-lot' || column === 'backlog' || (column === 'sprint' && sprintNumber !== undefined)) && !!onDragStart

    return (
        <div
            className={`bg-slate-800/80 border border-slate-700/70 rounded-lg p-2.5 flex flex-col gap-1.5 hover:border-slate-600 transition-colors ${isDraggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}`}
            draggable={isDraggable}
            onDragStart={isDraggable && onDragStart ? (e) => onDragStart(e, item, column, sprintNumber) : undefined}
        >
            <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] font-mono text-slate-500">#{item.id}</span>
                <span className={`text-[10px] font-semibold rounded-full px-1.5 py-px ${typeStyle(item.workItemType)}`}>
                    {item.workItemType === 'User Story' ? 'Story' : item.workItemType}
                </span>
                {priorityLabel(item.priority)}
                {autoBadges.map(b => (
                    <span key={b.label} className={`text-[10px] rounded-full px-1.5 py-px font-medium ${b.style}`}>{b.label}</span>
                ))}
            </div>
            <p className="text-xs text-slate-200 line-clamp-2 leading-snug">{item.title}</p>
            <BoardDeliveryStatus item={item} />
            <div className="flex items-center justify-between gap-1 mt-0.5">
                <span className="text-[10px] text-slate-500 truncate">{item.assignedTo ? item.assignedTo.split(' ')[0] : '—'}</span>
                {!feedback && column === 'ready' && !isSprintItem && (
                    <button
                        onClick={() => addMutation.mutate()}
                        disabled={isBusy}
                        className="shrink-0 rounded-full bg-emerald-700 px-2 py-px text-[10px] font-semibold text-white hover:bg-emerald-600 transition-colors disabled:opacity-50"
                    >
                        {addMutation.isPending ? '…' : 'Release ▶'}
                    </button>
                )}
            </div>
            {feedback && (
                <div className={[
                    'text-[10px] px-1.5 py-0.5 rounded flex items-center justify-between gap-1',
                    feedback.ok ? 'text-emerald-400 bg-emerald-950/40' : 'text-red-400 bg-red-950/40',
                ].join(' ')}>
                    <span className="truncate">{feedback.msg}</span>
                    <button onClick={() => setFeedback(null)} className="shrink-0 opacity-60 hover:opacity-100">✕</button>
                </div>
            )}
        </div>
    )
}

const KANBAN_COLS: { id: KanbanColumn; label: string; color: string }[] = [
    { id: 'parking-lot',  label: 'Parking Lot',       color: 'text-amber-300 border-amber-700/50' },
    { id: 'backlog',      label: 'Backlog',            color: 'text-slate-300 border-slate-600' },
    { id: 'sprint',       label: 'Sprint',             color: 'text-indigo-300 border-indigo-700/50' },
    { id: 'in-progress',  label: 'In Progress',        color: 'text-blue-300 border-blue-700/50' },
    { id: 'ready',        label: 'Ready to Release',   color: 'text-emerald-300 border-emerald-700/50' },
]

function KanbanView() {
    const queryClient = useQueryClient()
    const parkingQ = useQuery({ queryKey: ['releases-parking-lot'],  queryFn: releaseApi.getReleasesParkingLot,  staleTime: 60_000 })
    const backlogQ = useQuery({ queryKey: ['releases-backlog'],      queryFn: releaseApi.getReleasesBacklog,     staleTime: 60_000 })
    const inProgQ  = useQuery({ queryKey: ['releases-in-progress'],  queryFn: releaseApi.getReleasesInProgress,  staleTime: 60_000 })
    const readyQ   = useQuery({ queryKey: ['releases-ready'],        queryFn: releaseApi.getReleasesReady,       staleTime: 60_000 })
    const sprintQ  = useQuery({ queryKey: ['sprint-active'],          queryFn: releaseApi.getActiveSprints,       staleTime: 60_000 })

    const [dragSource, setDragSource] = useState<{ item: WorkItemSummary; col: KanbanColumn; sprintNumber?: number } | null>(null)
    const [dropTarget, setDropTarget] = useState<KanbanColumn | null>(null)
    const [sprintDropTarget, setSprintDropTarget] = useState<number | null>(null)
    const [collapsedSprints, setCollapsedSprints] = useState<Set<number>>(new Set())

    const parkMutation = useMutation({
        mutationFn: (id: number) => releaseApi.sendToParkingLot(id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['releases-backlog'] })
            void queryClient.invalidateQueries({ queryKey: ['releases-parking-lot'] })
        },
    })
    const promoteMutation = useMutation({
        mutationFn: (id: number) => releaseApi.promoteToBacklog(id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['releases-parking-lot'] })
            void queryClient.invalidateQueries({ queryKey: ['releases-backlog'] })
        },
    })
    const moveSprintItemMutation = useMutation({
        mutationFn: ({ targetSprint, workItemId }: { targetSprint: number; workItemId: number }) =>
            releaseApi.addItemsToSprint(targetSprint, { workItemIds: [workItemId] }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['sprint-active'] })
            void queryClient.invalidateQueries({ queryKey: ['sprint-current'] })
        },
    })
    const addBacklogToSprintMutation = useMutation({
        mutationFn: ({ targetSprint, workItemId }: { targetSprint: number; workItemId: number }) =>
            releaseApi.addItemsToSprint(targetSprint, { workItemIds: [workItemId] }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['sprint-active'] })
            void queryClient.invalidateQueries({ queryKey: ['releases-backlog'] })
        },
    })
    const removeSprintItemMutation = useMutation({
        mutationFn: (workItemId: number) =>
            releaseApi.removeItemsFromSprint({ workItemIds: [workItemId] }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['sprint-active'] })
            void queryClient.invalidateQueries({ queryKey: ['sprint-current'] })
            void queryClient.invalidateQueries({ queryKey: ['releases-backlog'] })
        },
    })

    function handleDragStart(e: React.DragEvent, item: WorkItemSummary, col: KanbanColumn, sprintNumber?: number) {
        e.dataTransfer.effectAllowed = 'move'
        setDragSource(sprintNumber !== undefined ? { item, col, sprintNumber } : { item, col })
    }

    function handleDrop(col: KanbanColumn) {
        if (!dragSource || dragSource.col === col) { setDragSource(null); setDropTarget(null); return }
        if (dragSource.col === 'backlog' && col === 'parking-lot') parkMutation.mutate(dragSource.item.id)
        if (dragSource.col === 'parking-lot' && col === 'backlog') promoteMutation.mutate(dragSource.item.id)
        if (dragSource.col === 'sprint' && col === 'backlog') removeSprintItemMutation.mutate(dragSource.item.id)
        setDragSource(null)
        setDropTarget(null)
    }

    const sprintPathPattern = SPRINT_PATH_PATTERN

    // Group all sprint items by sprint number (sorted descending so highest sprint first).
    const activeSprints = sprintQ.data ?? []
    const allSprintItems = activeSprints.flatMap(s =>
        s.items.map(si => ({
            item: {
                id: si.workItemId, title: si.title, workItemType: si.workItemType,
                state: si.adoState, priority: null, tags: null, assignedTo: null,
                iterationPath: null, createdDate: null, epicId: si.epicId ?? null, epicTitle: si.epicTitle ?? null,
            } as WorkItemSummary,
            sprintNumber: s.sprint.number,
        }))
    )
    const totalSprintItems = allSprintItems.length

    const sprintGroups = activeSprints
        .filter(s => s.items.length > 0)
        .map(s => ({
            number: s.sprint.number,
            status: s.sprint.status,
            items: s.items.map(si => ({
                id: si.workItemId, title: si.title, workItemType: si.workItemType,
                state: si.adoState, priority: null, tags: null, assignedTo: null,
                iterationPath: null, createdDate: null, epicId: si.epicId ?? null, epicTitle: si.epicTitle ?? null,
            } as WorkItemSummary)),
        }))

    const queries: Record<string, { isLoading: boolean; isError: boolean }> = {
        'parking-lot':  parkingQ,
        backlog:        backlogQ,
        sprint:         sprintQ,
        'in-progress':  inProgQ,
        ready:          readyQ,
    }

    return (
        <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
                {KANBAN_COLS.map(col => {
                    const q     = queries[col.id]!
                    let items: WorkItemSummary[]
                    if (col.id === 'sprint') {
                        items = allSprintItems.map(x => x.item)
                    } else {
                        const qData = col.id === 'parking-lot' ? parkingQ : col.id === 'backlog' ? backlogQ : col.id === 'in-progress' ? inProgQ : readyQ
                        const rawItems = qData.data?.items ?? []
                        // Filter sprint-assigned items from all non-sprint columns —
                        // sprint items are shown in the Sprint column, not scattered across others.
                        items = rawItems.filter(i => !i.iterationPath || !sprintPathPattern.test(i.iterationPath))
                    }

                    const canDrop = dragSource !== null && dragSource.col !== col.id &&
                        ((dragSource.col === 'backlog' && col.id === 'parking-lot') ||
                         (dragSource.col === 'parking-lot' && col.id === 'backlog') ||
                         (dragSource.col === 'sprint' && col.id === 'backlog') ||
                         (dragSource.col === 'backlog' && col.id === 'sprint'))

                    return (
                        <div
                            key={col.id}
                            className={`w-64 shrink-0 flex flex-col gap-2 rounded-lg transition-colors ${dropTarget === col.id && canDrop ? 'ring-2 ring-inset ring-indigo-500/50 bg-indigo-950/20' : ''}`}
                            onDragOver={canDrop ? (e) => { e.preventDefault(); setDropTarget(col.id) } : undefined}
                            onDragLeave={canDrop ? () => setDropTarget(null) : undefined}
                            onDrop={canDrop ? () => handleDrop(col.id) : undefined}
                        >
                            {/* Column header */}
                            <div className={`flex items-center gap-2 px-1 py-1.5 border-b ${col.color}`}>
                                <span className={`text-xs font-semibold ${col.color.split(' ')[0]}`}>
                                    {col.label}
                                </span>
                                <span className="text-[10px] text-slate-600 bg-slate-700/50 rounded-full px-1.5 py-0.5 ml-auto">
                                    {col.id === 'sprint' ? totalSprintItems : items.length}
                                </span>
                            </div>

                            {/* Cards */}
                            <div className="flex flex-col gap-2 p-1">
                                {q.isLoading ? (
                                    <div className="text-xs text-slate-600 animate-pulse py-6 text-center">Loading…</div>
                                ) : q.isError ? (
                                    <div className="text-xs text-red-400 py-6 text-center">Failed to load</div>
                                ) : col.id === 'sprint' ? (
                                    sprintGroups.length === 0 ? (
                                        <div className="text-xs text-slate-600 italic py-6 text-center">No active sprint</div>
                                    ) : (
                                        sprintGroups.map(group => {
                                            const collapsed = collapsedSprints.has(group.number)
                                            const canSprintToSprint = dragSource?.col === 'sprint' && dragSource.sprintNumber !== group.number
                                            const canBacklogToSprint = dragSource?.col === 'backlog' && group.status === 'Queued'
                                            const isStartedReject = dragSource?.col === 'backlog' && group.status === 'Started'
                                            const canReceiveDrop = canSprintToSprint || canBacklogToSprint
                                            const isSprintOver = sprintDropTarget === group.number
                                            return (
                                                <div
                                                    key={group.number}
                                                    className={`mb-2 last:mb-0 rounded transition-colors ${isSprintOver && canReceiveDrop ? 'ring-2 ring-inset ring-indigo-500/50 bg-indigo-950/20' : ''} ${isSprintOver && isStartedReject ? 'ring-2 ring-inset ring-red-500/40 bg-red-950/10' : ''}`}
                                                    onDragOver={(e) => {
                                                        if (canReceiveDrop) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setSprintDropTarget(group.number) }
                                                        else if (isStartedReject) { e.preventDefault(); e.dataTransfer.dropEffect = 'none'; setSprintDropTarget(group.number) }
                                                    }}
                                                    onDragLeave={() => setSprintDropTarget(null)}
                                                    onDrop={canReceiveDrop ? (e) => {
                                                        e.preventDefault()
                                                        setSprintDropTarget(null)
                                                        if (dragSource) {
                                                            if (canSprintToSprint) {
                                                                moveSprintItemMutation.mutate({ targetSprint: group.number, workItemId: dragSource.item.id })
                                                            } else if (canBacklogToSprint) {
                                                                addBacklogToSprintMutation.mutate({ targetSprint: group.number, workItemId: dragSource.item.id })
                                                            }
                                                        }
                                                        setDragSource(null)
                                                    } : (e) => { e.preventDefault(); setSprintDropTarget(null) }}
                                                >
                                                    <button
                                                        onClick={() => setCollapsedSprints(prev => {
                                                            const next = new Set(prev)
                                                            if (next.has(group.number)) { next.delete(group.number) } else { next.add(group.number) }
                                                            return next
                                                        })}
                                                        className="w-full flex items-center gap-1.5 px-1 py-1 mt-1 hover:bg-slate-700/20 rounded transition-colors"
                                                    >
                                                        <span className="text-[10px] text-slate-500">{collapsed ? '▸' : '▾'}</span>
                                                        <span className="text-[10px] font-semibold text-indigo-400 uppercase tracking-wider">
                                                            Sprint {group.number}
                                                        </span>
                                                        <span className="text-[9px] text-slate-600 bg-slate-700/50 rounded-full px-1 py-0.5">
                                                            {group.items.length}
                                                        </span>
                                                        {isSprintOver && isStartedReject && (
                                                            <span className="text-[9px] text-red-400 ml-1">✗ Started</span>
                                                        )}
                                                    </button>
                                                    {!collapsed && (
                                                        <div className="flex flex-col gap-1.5 mt-1">
                                                            {group.items.length === 0 ? (
                                                                <div className="text-xs text-slate-600 italic py-2 text-center">Empty</div>
                                                            ) : (
                                                                groupBoardItems(group.items).map(epic => (
                                                                    <BoardEpicGroup key={epic.epicId ?? 'ungrouped'} {...epic}>
                                                                        {epic.items.map(item => (
                                                                            <KanbanCard key={item.id} item={item} column={col.id} onDragStart={handleDragStart} sprintNumber={group.number} />
                                                                        ))}
                                                                    </BoardEpicGroup>
                                                                ))
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        })
                                    )
                                ) : items.length === 0 ? (
                                    <div className="text-xs text-slate-600 italic py-6 text-center">Empty</div>
                                ) : (
                                    groupBoardItems(items).map(epic => (
                                        <BoardEpicGroup key={epic.epicId ?? 'ungrouped'} {...epic}>
                                            {epic.items.map(item => (
                                                <KanbanCard key={item.id} item={item} column={col.id} onDragStart={handleDragStart} />
                                            ))}
                                        </BoardEpicGroup>
                                    ))
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

function elapsedSince(iso: string | null): string {
    if (!iso) return ''
    const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
    if (secs < 60)   return `${secs}s`
    if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`
    return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`
}

// ── Sprint panel (AB#438, AB#439, AB#432) ────────────────────────────────────

function SprintItemRow({ item, deferred, onToggleDefer, sprintNumber, dragEnabled, deferEnabled, onSendToBacklog }: {
    item: SprintStatusResponse['items'][number]
    deferred: boolean
    onToggleDefer: (id: number) => void
    sprintNumber?: number
    dragEnabled?: boolean
    deferEnabled?: boolean
    onSendToBacklog?: () => void
}) {
    const openModal  = useContext(WorkItemModalContext)
    const progressed = item.adoState === 'Active' || item.adoState === 'Resolved' || item.adoState === 'Closed'

    const moduleTags = (item.tags ?? '')
        .split(';')
        .map(t => t.trim())
        .filter(t => t.length > 0 && !/^Sprint\s*\d/i.test(t) && t.toLowerCase() !== 'sprint-deferred')

    function handleDragStart(e: React.DragEvent) {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('application/x-sprint-item', JSON.stringify({
            workItemId: item.workItemId,
            fromSprintNumber: sprintNumber ?? 0,
        }))
    }

    return (
        <div
            draggable={dragEnabled}
            onDragStart={dragEnabled ? handleDragStart : undefined}
            className={`group flex items-center gap-3 px-5 py-2.5 border-b border-slate-700/30 last:border-0 cursor-pointer hover:bg-slate-700/20 transition-colors ${progressed ? 'opacity-40' : ''} ${dragEnabled ? 'cursor-grab active:cursor-grabbing' : ''}`}
            onClick={() => openModal(item.workItemId)}
        >
            <span className="text-xs font-mono text-slate-500 w-12 shrink-0">#{item.workItemId}</span>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold shrink-0 ${typeStyle(item.workItemType)}`}>
                {item.workItemType === 'User Story' ? 'Story' : item.workItemType}
            </span>
            <span className={`flex-1 min-w-0 text-sm truncate ${progressed ? 'text-slate-400' : 'text-slate-200'}`}>{item.title}</span>
            {moduleTags.length > 0 && (
                <span className="hidden sm:flex items-center gap-1 shrink-0">
                    {moduleTags.slice(0, 3).map(tag => (
                        <span key={tag} className="rounded px-1.5 py-0.5 text-[9px] font-medium bg-slate-700/60 text-slate-400 border border-slate-600/50">
                            {tag}
                        </span>
                    ))}
                </span>
            )}
            <span className={`text-xs shrink-0 w-16 text-right font-mono ${
                item.adoState === 'Resolved' ? 'text-emerald-400' :
                item.adoState === 'Active'   ? 'text-amber-400' : 'text-slate-500'
            }`}>{item.adoState}</span>
            {item.mergedToSprint ? (
                <span className="text-[10px] text-emerald-400 shrink-0 w-16 text-right">✓ merged</span>
            ) : item.deferred || deferred ? (
                <span className="text-[10px] text-slate-500 italic shrink-0 w-16 text-right">deferred</span>
            ) : (
                <span className="text-[10px] text-amber-400/70 shrink-0 w-16 text-right">pending</span>
            )}
            {deferEnabled && (
                <button
                    onClick={e => { e.stopPropagation(); onToggleDefer(item.workItemId) }}
                    title={deferred ? 'Remove from deferred list' : 'Defer this item from release'}
                    className={[
                        'opacity-0 group-hover:opacity-100 transition-opacity text-[10px] rounded px-1.5 py-0.5 shrink-0',
                        deferred
                            ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                            : 'bg-slate-800 text-slate-500 hover:text-slate-300 ring-1 ring-slate-700',
                    ].join(' ')}
                >
                    {deferred ? '↩ un-defer' : '↷ defer'}
                </button>
            )}
            {onSendToBacklog && (
                <button
                    onClick={e => { e.stopPropagation(); onSendToBacklog() }}
                    title="Move back to backlog"
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] rounded px-1.5 py-0.5 shrink-0 bg-slate-800 text-slate-500 hover:text-slate-300 hover:bg-slate-700 ring-1 ring-slate-700"
                >
                    ← Backlog
                </button>
            )}
        </div>
    )
}

function NoSprintPanel() {
    const queryClient = useQueryClient()
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
    const [search, setSearch] = useState('')

    const nextNumberQ = useQuery({
        queryKey: ['sprint-next-number'],
        queryFn: releaseApi.getNextSprintNumber,
        staleTime: 60_000,
    })
    const [sprintNumber, setSprintNumber] = useState<number | null>(null)
    const resolvedNumber = sprintNumber ?? nextNumberQ.data?.nextNumber ?? 1

    const { data: backlogData } = useQuery({
        queryKey: ['releases-backlog'],
        queryFn: releaseApi.getReleasesBacklog,
        staleTime: 30_000,
    })

    const sprintPathPattern = SPRINT_PATH_PATTERN
    const backlogItems = (backlogData?.items ?? []).filter(
        i => !i.iterationPath || !sprintPathPattern.test(i.iterationPath)
    )
    const filteredBacklogItems = search
        ? backlogItems.filter(i => i.title.toLowerCase().includes(search.toLowerCase()) || String(i.id).includes(search))
        : backlogItems

    const toggleItem = (id: number) =>
        setSelectedIds(prev => {
            const next = new Set(prev)
            if (next.has(id)) { next.delete(id) } else { next.add(id) }
            return next
        })

    const toggleAll = () =>
        setSelectedIds(prev => prev.size === filteredBacklogItems.length ? new Set() : new Set(filteredBacklogItems.map(i => i.id)))

    const planMutation = useMutation({
        mutationFn: () => releaseApi.planSprint({
            sprintNumber: resolvedNumber,
            workItemIds: [...selectedIds],
        }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['sprint-current'] })
            void queryClient.invalidateQueries({ queryKey: ['sprint-active'] })
            void queryClient.invalidateQueries({ queryKey: ['sprint-next-number'] })
        },
    })

    useEffect(() => {
        if (!planMutation.isSuccess) return
        const t = setTimeout(() => { planMutation.reset(); setSelectedIds(new Set()) }, 2000)
        return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planMutation.isSuccess])

    return (
        <div className="flex flex-col gap-6 py-8 max-w-2xl mx-auto">
            <div>
                <p className="text-slate-300 font-medium">Plan Sprint {resolvedNumber}</p>
                <p className="text-slate-500 text-sm mt-0.5">
                    Select backlog items to include, then create the sprint. You can start it (create the git branch) from the sprint card.
                </p>
            </div>

            {backlogItems.length > 0 && (
                <div className="rounded-xl border border-slate-700 bg-slate-800/50 divide-y divide-slate-700/50">
                    <div className="flex items-center gap-3 px-4 py-2.5">
                        <input
                            type="checkbox"
                            checked={selectedIds.size === filteredBacklogItems.length && filteredBacklogItems.length > 0}
                            onChange={toggleAll}
                            className="accent-indigo-500"
                        />
                        <input
                            type="text"
                            placeholder="Search by # or title…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="flex-1 bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-1 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                        />
                        <span className="text-xs text-slate-400 font-medium shrink-0">
                            {selectedIds.size > 0 ? `${selectedIds.size} selected` : `${filteredBacklogItems.length} items`}
                        </span>
                    </div>
                    {filteredBacklogItems.map(item => (
                        <label key={item.id} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-700/30">
                            <input
                                type="checkbox"
                                checked={selectedIds.has(item.id)}
                                onChange={() => toggleItem(item.id)}
                                className="accent-indigo-500 shrink-0"
                            />
                            <span className="font-mono text-xs font-bold text-indigo-400 shrink-0 w-14">#{item.id}</span>
                            <span className="text-sm text-slate-200 truncate flex-1 min-w-0">{item.title}</span>
                            <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${typeStyle(item.workItemType)}`}>{item.workItemType}</span>
                        </label>
                    ))}
                    {filteredBacklogItems.length === 0 && (
                        <div className="px-4 py-4 text-center text-xs text-slate-500">No items match your search.</div>
                    )}
                </div>
            )}

            <div className="flex items-center gap-3">
                <label className="text-sm text-slate-400 shrink-0">Sprint number</label>
                <input
                    type="number"
                    min={1}
                    value={resolvedNumber}
                    onChange={e => setSprintNumber(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20 rounded-lg bg-slate-800 border border-slate-600 px-3 py-1.5 text-sm text-slate-100 text-center focus:outline-none focus:border-indigo-500"
                />
                <button
                    onClick={() => planMutation.mutate()}
                    disabled={planMutation.isPending || planMutation.isSuccess}
                    className="rounded-lg bg-indigo-600 px-5 py-1.5 text-sm font-semibold text-white hover:bg-indigo-500 transition-colors disabled:opacity-50"
                >
                    {planMutation.isPending
                        ? 'Creating…'
                        : planMutation.isSuccess
                            ? 'Created ✓'
                            : selectedIds.size > 0
                                ? `Create Sprint ${resolvedNumber} (${selectedIds.size} item${selectedIds.size === 1 ? '' : 's'})`
                                : `Create Sprint ${resolvedNumber}`}
                </button>
            </div>

            {planMutation.isError && (
                <p className="text-red-400 text-sm">
                    {planMutation.error instanceof Error ? planMutation.error.message : 'Failed to create sprint'}
                </p>
            )}
        </div>
    )
}

type StageStatus = 'pending' | 'running' | 'success' | 'failed' | 'skipped'

interface PipelineStage {
    id: string
    label: string
    status: StageStatus
    detail?: string | undefined
    url?: string | null | undefined
}

function derivePipelineStages(pipeline: SprintReleasePipeline | null | undefined): PipelineStage[] {
    const runs      = pipeline?.runs ?? []
    const merged    = pipeline?.prStatus === 'completed'
    const abandoned = pipeline?.prStatus === 'abandoned'

    // When not merged, runs = pre-merge PR policy checks.
    // When merged, runs = post-merge image pipeline builds.
    // Sort newest-first by buildNumber (zero-padded date strings like "20250512.3").
    const sortedRuns = [...runs].sort((a, b) => (b.buildNumber ?? '').localeCompare(a.buildNumber ?? ''))
    const latestRun  = sortedRuns[0]
    const anyRunning = runs.some(r => r.status === 'inProgress')

    // Checks = pre-merge PR policy CI. When merged the checks all passed (merge proves it).
    // prMergeStatus is the most reliable signal — ADO sets it regardless of whether build runs are queryable.
    const prMergeStatus = pipeline?.prMergeStatus ?? null
    const checksStatus: StageStatus =
        merged                                                                          ? 'success'
        : abandoned                                                                     ? 'failed'
        : prMergeStatus === 'rejectedByPolicy' || prMergeStatus === 'failure'
          || prMergeStatus === 'conflicts'                                               ? 'failed'
        : prMergeStatus === 'succeeded'                                                 ? 'success'
        : prMergeStatus === 'queued'                                                    ? 'success'
        : !latestRun                                                                    ? 'pending'
        : anyRunning || latestRun.status === 'inProgress'                               ? 'running'
        : latestRun.result === 'failed'                                                 ? 'failed'
        : latestRun.result === 'canceled'                                               ? 'failed'
        : latestRun.result === 'succeeded'                                              ? 'success'
        : latestRun.result === 'partiallySucceeded'                                     ? 'success'
        : latestRun.status === 'notStarted'                                             ? 'pending'
        : 'pending'

    // Detail only meaningful pre-merge (post-merge runs belong to Pipeline stage).
    const checksDetailRun = merged ? undefined : (anyRunning ? runs.find(r => r.status === 'inProgress') : latestRun)

    const mergeStatus: StageStatus = abandoned ? 'failed' : merged ? 'success' : prMergeStatus === 'queued' ? 'running' : 'pending'

    // Pipeline = post-merge image build. Runs are empty until the PR is merged.
    const pipelineStatus: StageStatus =
        !merged                                             ? 'skipped'
        : !latestRun                                        ? 'pending'
        : anyRunning || latestRun.status === 'inProgress'   ? 'running'
        : latestRun.result === 'succeeded'                  ? 'success'
        : latestRun.result === 'failed'                     ? 'failed'
        : latestRun.result === 'canceled'                   ? 'failed'
        : latestRun.result === 'partiallySucceeded'         ? 'success'
        : 'pending'

    const pipelineDetailRun = !merged ? undefined : (anyRunning ? runs.find(r => r.status === 'inProgress') : latestRun)

    // GitOps PR = automated PR raised in Marque-gitops repo by the pipeline.
    const gitOpsPr = pipeline?.gitOpsPrStatus ?? null
    const gitOpsPrStatus: StageStatus =
        !merged                                   ? 'skipped'
        : !gitOpsPr || gitOpsPr === 'none'        ? 'pending'
        : gitOpsPr === 'abandoned'                ? 'failed'
        : gitOpsPr === 'completed'                ? 'success'
        : 'running'

    // GitOps Merged = GitOps PR auto-completed; ArgoCD reconciles from here.
    const gitOpsMerged = pipeline?.gitOpsMerged ?? false
    const gitOpsMergedStatus: StageStatus =
        !merged                                   ? 'skipped'
        : !gitOpsPr || gitOpsPr === 'none'        ? 'skipped'
        : gitOpsPr === 'abandoned'                ? 'failed'
        : gitOpsMerged                            ? 'success'
        : 'pending'

    const devDeployed = pipeline?.devDeployed ?? false
    const qaDeployed  = pipeline?.qaDeployed  ?? false

    const devStatus: StageStatus =
        !merged       ? 'skipped'
        : devDeployed ? 'success'
        : 'running'

    const qaStatus: StageStatus =
        !devDeployed  ? 'skipped'
        : qaDeployed  ? 'success'
        : 'running'

    return [
        { id: 'checks',       label: 'Checks',       status: checksStatus,      detail: checksDetailRun?.name,   url: checksDetailRun?.webUrl },
        { id: 'merge',        label: 'Merged',        status: mergeStatus,       detail: pipeline?.prMergeCommitSha?.slice(0, 7) },
        { id: 'pipeline',     label: 'Pipeline',      status: pipelineStatus,    detail: pipelineDetailRun?.name, url: pipelineDetailRun?.webUrl },
        { id: 'gitops-pr',    label: 'GitOps PR',    status: gitOpsPrStatus },
        { id: 'gitops-merge', label: 'GitOps Merged', status: gitOpsMergedStatus },
        { id: 'dev',          label: 'Dev',           status: devStatus },
        { id: 'qa',           label: 'QA',            status: qaStatus },
    ]
}

function stageDescriptionText(
    stages: PipelineStage[],
    releasePrId: number,
    pipeline: SprintReleasePipeline | null | undefined,
): string {
    const checks      = stages.find(s => s.id === 'checks')!
    const merge       = stages.find(s => s.id === 'merge')!
    const pipeStage   = stages.find(s => s.id === 'pipeline')!
    const gitopsPr    = stages.find(s => s.id === 'gitops-pr')!
    const gitopsMerge = stages.find(s => s.id === 'gitops-merge')!
    const dev         = stages.find(s => s.id === 'dev')!
    const qa          = stages.find(s => s.id === 'qa')!

    if (merge.status === 'failed') {
        return `Sprint PR #${releasePrId} was abandoned`
    }
    if (merge.status === 'success') {
        if (qa.status === 'success') {
            return `Sprint PR #${releasePrId} merged and deployed to QA`
        }
        if (dev.status === 'success') {
            return `Sprint PR #${releasePrId} merged and deployed to dev`
        }
        if (gitopsMerge.status === 'success') {
            return `Sprint PR #${releasePrId} merged — GitOps applied, deploying to dev`
        }
        if (gitopsPr.status === 'running') {
            return `Sprint PR #${releasePrId} merged — GitOps PR open, awaiting auto-complete`
        }
        if (pipeStage.status === 'success') {
            return `Sprint PR #${releasePrId} merged — pipeline done, raising GitOps PR`
        }
        if (pipeStage.status === 'running') {
            const name = pipeline?.runs.find(r => r.status === 'inProgress')?.name
            return `Sprint PR #${releasePrId} merged — ${name ? `running ${name}` : 'pipeline running'}`
        }
        return `Sprint PR #${releasePrId} merged — waiting for pipeline`
    }
    if (checks.status === 'failed') {
        return `Sprint PR #${releasePrId} raised — checks failed, fix required`
    }
    if (checks.status === 'running') {
        const name = pipeline?.runs.find(r => r.status === 'inProgress')?.name
        return `Sprint PR #${releasePrId} raised — ${name ? `running ${name}` : 'checks running'}`
    }
    const checksPassed = pipeline?.runs.some(r => r.result === 'succeeded')
    if (checksPassed) {
        return `Sprint PR #${releasePrId} raised — checks passed, awaiting merge`
    }
    return `Sprint PR #${releasePrId} raised — waiting for checks`
}

function StageCircle({ stage, onClick }: { stage: PipelineStage; onClick?: () => void }) {
    const ringClass =
        stage.status === 'success'  ? 'bg-emerald-500 ring-emerald-400/40'
        : stage.status === 'failed'  ? 'bg-red-500 ring-red-400/40'
        : stage.status === 'running' ? 'bg-amber-400 animate-pulse ring-amber-400/40'
        : stage.status === 'pending' ? 'bg-slate-700 ring-slate-500/20'
        : /* skipped */               'bg-slate-800 ring-slate-700/20'

    const icon =
        stage.status === 'success'  ? '✓'
        : stage.status === 'failed'  ? '✗'
        : stage.status === 'running' ? '⟳'
        : stage.status === 'skipped' ? '—'
        : null

    const spinClass = stage.status === 'pending' ? 'animate-spin' : ''

    const circle = (
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ring-4 ${ringClass} ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
            onClick={onClick}
        >
            {icon !== null ? icon : <span className={`text-slate-400 ${spinClass}`}>⟳</span>}
        </div>
    )

    if (stage.url) {
        return (
            <a href={stage.url} target="_blank" rel="noreferrer" className="hover:opacity-80 transition-opacity">
                {circle}
            </a>
        )
    }
    return circle
}

function StageArrow({ fromStatus }: { fromStatus: StageStatus }) {
    const color =
        fromStatus === 'success' ? '#10b981'
        : fromStatus === 'failed' ? 'rgba(239,68,68,0.4)'
        : '#334155'
    return (
        <svg width="36" height="14" viewBox="0 0 36 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0 self-center">
            <line x1="0" y1="7" x2="28" y2="7" stroke={color} strokeWidth="1.5" />
            <polygon points="28,3 36,7 28,11" fill={color} />
        </svg>
    )
}

function SprintReleaseVisualizer({
    sprintNumber,
    releasePrId,
    releasePrUrl,
    pipeline,
}: {
    sprintNumber: number
    releasePrId: number
    releasePrUrl: string | null
    pipeline: SprintReleasePipeline | null | undefined
}) {
    const storageKey = `sprint-pipeline-expanded-${sprintNumber}`
    const [expanded, setExpanded] = useState(() => {
        try { return localStorage.getItem(storageKey) === 'true' } catch { return false }
    })
    const toggle = () => {
        const next = !expanded
        setExpanded(next)
        try { localStorage.setItem(storageKey, String(next)) } catch { /* ignore */ }
    }

    const stages = derivePipelineStages(pipeline)
    const description = stageDescriptionText(stages, releasePrId, pipeline)
    const isActive = pipeline?.prStatus === 'active' || pipeline?.prStatus === undefined
    const dotClass = isActive ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'

    return (
        <div className="flex flex-col gap-2">
            {/* Descriptive sentence row */}
            <div className="flex items-center gap-3">
                <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${dotClass}`} />
                <span className={`text-sm font-medium ${isActive ? 'text-amber-300' : 'text-emerald-300'}`}>
                    {description}
                </span>
                {releasePrUrl && (
                    <a
                        href={releasePrUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-auto text-xs text-indigo-400 hover:text-indigo-300 underline shrink-0"
                    >
                        View PR ↗
                    </a>
                )}
                <button
                    onClick={toggle}
                    className="text-xs text-slate-500 hover:text-slate-300 transition-colors shrink-0 font-mono"
                >
                    {expanded ? '▾ hide' : '▸ pipeline'}
                </button>
            </div>

            {/* Collapsible stage flow — two-row layout: circles on top, labels below */}
            {expanded && (
                <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 px-4 sm:px-6 py-4">
                    {/* Circle row */}
                    <div className="flex items-center">
                        {stages.map((stage, i) => (
                            <div key={stage.id} className="flex items-center">
                                <StageCircle stage={stage} />
                                {i < stages.length - 1 && (
                                    <StageArrow fromStatus={stage.status} />
                                )}
                            </div>
                        ))}
                    </div>
                    {/* Label row — use same spacing as circle row */}
                    <div className="flex items-start mt-2">
                        {stages.map((stage, i) => {
                            const labelClass =
                                stage.status === 'success'  ? 'text-emerald-400'
                                : stage.status === 'failed'  ? 'text-red-400'
                                : stage.status === 'running' ? 'text-amber-300'
                                : stage.status === 'pending' ? 'text-slate-400'
                                : 'text-slate-600'
                            return (
                                <div key={stage.id} className="flex items-start">
                                    <div className="w-7 flex flex-col items-center gap-0.5">
                                        <span className={`text-[10px] font-medium text-center leading-tight ${labelClass}`}>{stage.label}</span>
                                        {stage.detail && (
                                            <span className="text-[9px] text-slate-500 text-center leading-tight max-w-[56px] truncate">{stage.detail}</span>
                                        )}
                                    </div>
                                    {i < stages.length - 1 && (
                                        <div className="w-9 shrink-0" />
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

function AddItemModal({ sprintNumber, existingIds, onClose, onAdded }: {
    sprintNumber: number
    existingIds: Set<number>
    onClose: () => void
    onAdded: () => void
}) {
    const [search, setSearch] = useState('')
    const [selected, setSelected] = useState<Set<number>>(new Set())
    const queryClient = useQueryClient()

    const backlogQ = useQuery({
        queryKey: ['releases-backlog-for-sprint-picker'],
        queryFn: releaseApi.getReleasesBacklog,
        staleTime: 30_000,
    })

    const addMutation = useMutation({
        mutationFn: () => releaseApi.addItemsToSprint(sprintNumber, { workItemIds: [...selected] }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['sprint-current'] })
            void queryClient.invalidateQueries({ queryKey: ['sprint-active'] })
            onAdded()
            onClose()
        },
    })

    const items = (backlogQ.data?.items ?? []).filter(
        i => !existingIds.has(i.id) && (!i.iterationPath || !SPRINT_PATH_PATTERN.test(i.iterationPath))
    )
    const filtered = search
        ? items.filter(i => i.title.toLowerCase().includes(search.toLowerCase()) || String(i.id).includes(search))
        : items

    function toggle(id: number) {
        setSelected(prev => {
            const next = new Set(prev)
            if (next.has(id)) { next.delete(id) } else { next.add(id) }
            return next
        })
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
            <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg mx-4 flex flex-col max-h-[70vh]" onClick={e => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold text-slate-100">Add backlog items to Sprint {sprintNumber}</span>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition-colors text-lg leading-none">✕</button>
                </div>
                <div className="px-5 py-3 border-b border-slate-700/60">
                    <input
                        autoFocus
                        type="text"
                        placeholder="Search…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                    />
                </div>
                <div className="overflow-y-auto flex-1 divide-y divide-slate-700/30">
                    {backlogQ.isPending && (
                        <div className="px-5 py-6 text-center text-sm text-slate-500">Loading…</div>
                    )}
                    {!backlogQ.isPending && filtered.length === 0 && (
                        <div className="px-5 py-6 text-center text-sm text-slate-500">No backlog items available</div>
                    )}
                    {filtered.map(item => (
                        <label key={item.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-700/20 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={selected.has(item.id)}
                                onChange={() => toggle(item.id)}
                                className="accent-indigo-500 shrink-0"
                            />
                            <span className="font-mono text-xs font-bold text-indigo-400 shrink-0 w-14">#{item.id}</span>
                            <span className="text-sm text-slate-200 truncate flex-1 min-w-0">{item.title}</span>
                            <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${typeStyle(item.workItemType)}`}>{item.workItemType}</span>
                        </label>
                    ))}
                </div>
                <div className="px-5 py-4 border-t border-slate-700 flex items-center justify-between gap-3">
                    <span className="text-xs text-slate-500">
                        {selected.size > 0 ? `${selected.size} item${selected.size !== 1 ? 's' : ''} selected` : 'Select items to add'}
                    </span>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="rounded-lg bg-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-600 transition-colors">
                            Cancel
                        </button>
                        <button
                            onClick={() => addMutation.mutate()}
                            disabled={selected.size === 0 || addMutation.isPending}
                            className="rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {addMutation.isPending ? 'Adding…' : 'Add to Sprint'}
                        </button>
                    </div>
                    {addMutation.isError && (
                        <span className="text-xs text-red-400 ml-2">
                            {addMutation.error instanceof Error ? addMutation.error.message : 'Failed to add items'}
                        </span>
                    )}
                </div>
            </div>
        </div>
    )
}

// ── Live Session Panel ────────────────────────────────────────────────────────

function LiveSessionPanel({ sprintNumber, onClose }: { sprintNumber: number; onClose: () => void }) {
    const [chunks, setChunks] = useState<SprintSessionChunk[]>([])
    const [connected, setConnected] = useState(false)
    const [sessionEnded, setSessionEnded] = useState(false)
    const bottomRef = useRef<HTMLDivElement>(null)

    const { data: sessionData, isLoading } = useQuery({
        queryKey: ['sprint-session', sprintNumber],
        queryFn: () => releaseApi.getSprintSession(sprintNumber),
        retry: false,
    })

    useEffect(() => {
        if (sessionData?.chunks)
        {
            setChunks(sessionData.chunks)
        }
    }, [sessionData])

    const handleClose = useCallback(() => onClose(), [onClose])
    useEscapeKey(handleClose)

    useEffect(() => {
        const connection = new signalR.HubConnectionBuilder()
            .withUrl('/hubs/sprint-session')
            .withAutomaticReconnect()
            .build()

        connection.on('SessionChunk', (evt: { sessionId: string; content: string; chunkType: string; createdAt: string }) => {
            setChunks(prev => [...prev, {
                id: Date.now(),
                sessionId: evt.sessionId,
                content: evt.content,
                chunkType: evt.chunkType,
                createdAt: evt.createdAt,
            }])
        })

        connection.on('SessionEnded', () => {
            setSessionEnded(true)
        })

        connection.start()
            .then(() => {
                setConnected(true)
                return connection.invoke('JoinSprint', sprintNumber)
            })
            .catch(() => { /* hub unavailable — degrade gracefully */ })

        return () => {
            connection.invoke('LeaveSprint', sprintNumber).catch(() => { /* ignore */ })
            void connection.stop()
        }
    }, [sprintNumber])

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [chunks])

    const session = sessionData?.session

    return createPortal(
        <div className="fixed inset-0 z-50 flex">
            <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div className="w-full max-w-2xl bg-slate-900 border-l border-slate-700 flex flex-col overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700 shrink-0">
                    <div>
                        <p className="text-sm font-semibold text-slate-100">
                            🔴 Live Session — Sprint {sprintNumber}
                        </p>
                        {session && (
                            <p className="text-xs text-slate-400 mt-0.5">
                                Started by {session.initiatorEmail.split('@')[0]} · {elapsedSince(session.startedAt)}
                                {!connected && <span className="ml-2 text-amber-400">connecting…</span>}
                                {sessionEnded && <span className="ml-2 text-slate-500">· session ended</span>}
                            </p>
                        )}
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-200 text-lg leading-none">✕</button>
                </div>
                {/* Chunk stream */}
                <div className="flex-1 overflow-y-auto p-4 font-mono text-xs text-slate-300 space-y-1.5">
                    {isLoading && (
                        <p className="text-slate-500 animate-pulse">Loading session…</p>
                    )}
                    {!isLoading && !session && (
                        <p className="text-slate-500 italic">No active session for this sprint.</p>
                    )}
                    {chunks.map((chunk) => (
                        <div key={chunk.id} className={[
                            'rounded px-2 py-1 whitespace-pre-wrap break-all',
                            chunk.chunkType === 'tool_call'   ? 'text-amber-300 bg-amber-950/30'
                            : chunk.chunkType === 'tool_result' ? 'text-slate-500 bg-slate-800/50'
                            : 'text-slate-200',
                        ].join(' ')}>
                            {chunk.chunkType === 'tool_call' && <span className="text-amber-500 mr-1">⚙</span>}
                            {chunk.content}
                        </div>
                    ))}
                    {chunks.length === 0 && session && !isLoading && (
                        <p className="text-slate-500 italic">Waiting for session output…</p>
                    )}
                    <div ref={bottomRef} />
                </div>
            </div>
        </div>,
        document.body
    )
}

function SingleSprintPanel({ data, blockedBySprintNumber, currentUserEmail }: { data: SprintStatusResponse; blockedBySprintNumber?: number; currentUserEmail?: string }) {
    const queryClient = useQueryClient()
    const [deferIds, setDeferIds] = useState<Set<number>>(new Set())
    const [elapsed, setElapsed] = useState(0)
    const [result, setResult] = useState<AddSprintToReleaseResult | null>(null)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [isDragOver, setIsDragOver] = useState(false)
    const [addItemOpen, setAddItemOpen] = useState(false)
    const [liveSessionOpen, setLiveSessionOpen] = useState(false)
    const [itemSearch, setItemSearch] = useState('')
    const [pendingDrop, setPendingDrop] = useState<{ workItemId: number; hasDeps: boolean } | null>(null)
    const [itemsExpanded, setItemsExpanded] = useState(() => {
        try { return localStorage.getItem(`sprint-items-expanded-${data.sprint.number}`) !== 'false' } catch { return true }
    })

    const toggleItemsExpanded = () => setItemsExpanded(v => {
        try { localStorage.setItem(`sprint-items-expanded-${data.sprint.number}`, String(!v)) } catch { /* ignore */ }
        return !v
    })

    const invalidateSprints = () => {
        void queryClient.invalidateQueries({ queryKey: ['sprint-current'] })
        void queryClient.invalidateQueries({ queryKey: ['sprint-active'] })
    }

    const moveItemMutation = useMutation({
        mutationFn: (workItemId: number) =>
            releaseApi.addItemsToSprint(data.sprint.number, { workItemIds: [workItemId] }),
        onSuccess: invalidateSprints,
    })

    const addToReleaseMutation = useMutation({
        mutationFn: () => releaseApi.addSprintToRelease({
            sprintNumber: data.sprint.number,
            ...(deferIds.size > 0 ? { deferItemIds: [...deferIds] } : {}),
        }),
        onSuccess: (r) => { setResult(r); invalidateSprints() },
    })

    const startSprintMutation = useMutation({
        mutationFn: () => releaseApi.startSprint(data.sprint.number),
        onSuccess: invalidateSprints,
    })

    const removeFromSprintMutation = useMutation({
        mutationFn: (workItemId: number) =>
            releaseApi.removeItemsFromSprint({ workItemIds: [workItemId] }),
        onSuccess: () => {
            invalidateSprints()
            void queryClient.invalidateQueries({ queryKey: ['releases-backlog'] })
        },
    })

    const lockMutation = useMutation({
        mutationFn: () => releaseApi.lockSprint(data.sprint.number),
        onSuccess: invalidateSprints,
    })

    const unlockMutation = useMutation({
        mutationFn: () => releaseApi.unlockSprint(data.sprint.number),
        onSuccess: invalidateSprints,
    })

    const deleteMutation = useMutation({
        mutationFn: () => releaseApi.deleteSprint(data.sprint.number),
        onSuccess: () => {
            setConfirmDelete(false)
            invalidateSprints()
            void queryClient.invalidateQueries({ queryKey: ['releases-backlog'] })
            void queryClient.invalidateQueries({ queryKey: ['sprint-available'] })
            void queryClient.invalidateQueries({ queryKey: ['sprint-next-number'] })
        },
    })

    // Tick elapsed counter while sprint PR CI is in progress.
    useEffect(() => {
        if (!data.sprint.prRaisedAt) return
        const id = setInterval(() => setElapsed(e => e + 1), 1000)
        return () => clearInterval(id)
    }, [data.sprint.prRaisedAt])
    void elapsed

    function toggleDefer(id: number) {
        setDeferIds(prev => {
            const next = new Set(prev)
            if (next.has(id)) { next.delete(id) } else { next.add(id) }
            return next
        })
    }

    const { sprint, items, releasePrId, releasePrUrl } = data
    const prRaised    = !!releasePrId
    const mergedCount = items.filter(i => i.mergedToSprint).length
    const totalCount  = items.length
    const allResolved = items.every(i => i.adoState === 'Resolved' || i.deferred || deferIds.has(i.workItemId))

    const existingItemIds = new Set(items.map(i => i.workItemId))

    const isMySession     = !!sprint.workingDeveloper && sprint.workingDeveloper === currentUserEmail
    const sessionInProgress = !!sprint.workingDeveloper && !isMySession

    return (
        <div className="flex flex-col gap-4">
            {addItemOpen && (
                <AddItemModal
                    sprintNumber={sprint.number}
                    existingIds={existingItemIds}
                    onClose={() => setAddItemOpen(false)}
                    onAdded={invalidateSprints}
                />
            )}
            {liveSessionOpen && (
                <LiveSessionPanel
                    sprintNumber={sprint.number}
                    onClose={() => setLiveSessionOpen(false)}
                />
            )}
            {/* Sprint header */}
            <div className="rounded-xl border border-slate-700 bg-slate-800/40 p-5 flex flex-col gap-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                            {sprint.status === 'Queued' ? 'Queued sprint'
                                : sprint.status === 'Started' ? 'Started sprint'
                                : sprint.status === 'Pushed' ? 'Pushed sprint'
                                : sprint.status === 'Released' ? 'Released sprint'
                                : 'Active sprint'}
                        </p>
                        <p className="text-base font-semibold text-slate-100 mt-0.5">
                            Sprint {sprint.number}
                            {sprint.locked && <span className="ml-2 text-amber-400 text-sm" title="Sprint is locked — item list is frozen">🔒</span>}
                        </p>
                        <p className="text-xs font-mono text-indigo-300 mt-0.5">{sprint.branchName}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                            <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-semibold ${sprint.branchExists ? 'bg-emerald-900/50 text-emerald-400' : 'bg-slate-700/60 text-slate-500'}`}>
                                {sprint.branchExists ? '✓' : '✗'} branch
                            </span>
                            <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-semibold ${sprint.iterationExists ? 'bg-emerald-900/50 text-emerald-400' : 'bg-red-900/50 text-red-400'}`}>
                                {sprint.iterationExists ? '✓' : '✗'} iteration
                            </span>
                        </div>
                        {sprint.workingDeveloper && (
                            <p className="text-[10px] text-slate-400 mt-1">
                                🧑‍💻 {sprint.workingDeveloper.split('@')[0]}
                            </p>
                        )}
                    </div>
                    <div className="text-right">
                        {sprint.status === 'Queued'
                            ? <p className="text-xs text-slate-500">{totalCount} item{totalCount !== 1 ? 's' : ''} queued</p>
                            : <p className="text-xs text-slate-500">{mergedCount} / {totalCount} merged to sprint</p>
                        }
                        {sprint.prRaisedAt && (
                            <p className="text-xs text-amber-300 mt-0.5">
                                PR raised · CI running {elapsedSince(sprint.prRaisedAt)} elapsed
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-3 mt-1 pt-2 border-t border-slate-700/40">
                    {sprint.status === 'Queued' ? (
                        <>
                            <button
                                onClick={() => startSprintMutation.mutate()}
                                disabled={startSprintMutation.isPending || !!blockedBySprintNumber}
                                title={blockedBySprintNumber ? `Sprint ${blockedBySprintNumber} must deploy to dev before this sprint can start` : undefined}
                                className="rounded-lg bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {startSprintMutation.isPending ? 'Starting…' : 'Start Sprint ▶'}
                            </button>
                            {blockedBySprintNumber ? (
                                <span className="text-xs text-amber-400">
                                    Sprint {blockedBySprintNumber} is still in flight — wait for it to deploy to dev first.
                                </span>
                            ) : (
                                <span className="text-xs text-slate-500">
                                    Creates the <span className="font-mono">{sprint.branchName}</span> branch
                                </span>
                            )}
                            {startSprintMutation.isError && (
                                <span className="text-xs text-red-400">
                                    {startSprintMutation.error instanceof Error
                                        ? startSprintMutation.error.message
                                        : 'Failed to start sprint'}
                                </span>
                            )}
                            <span className="ml-auto flex items-center gap-2">
                                {sprint.locked ? (
                                    <button
                                        onClick={() => unlockMutation.mutate()}
                                        disabled={unlockMutation.isPending}
                                        className="rounded-lg bg-amber-700/40 px-3 py-1 text-xs text-amber-400 hover:bg-amber-700/60 transition-colors disabled:opacity-50"
                                    >
                                        {unlockMutation.isPending ? 'Unlocking…' : '🔓 Unlock'}
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => lockMutation.mutate()}
                                        disabled={lockMutation.isPending}
                                        className="rounded-lg bg-slate-700 px-3 py-1 text-xs text-slate-400 hover:bg-slate-600 transition-colors disabled:opacity-50"
                                    >
                                        {lockMutation.isPending ? 'Locking…' : '🔒 Lock'}
                                    </button>
                                )}
                                {confirmDelete ? (
                                    <>
                                        <span className="text-xs text-red-400">Un-assign all items?</span>
                                        <button
                                            onClick={() => deleteMutation.mutate()}
                                            disabled={deleteMutation.isPending}
                                            className="rounded-lg bg-red-700 px-3 py-1 text-xs font-semibold text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                                        >
                                            {deleteMutation.isPending ? 'Deleting…' : 'Confirm'}
                                        </button>
                                        <button
                                            onClick={() => setConfirmDelete(false)}
                                            className="rounded-lg bg-slate-700 px-3 py-1 text-xs text-slate-300 hover:bg-slate-600 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => setConfirmDelete(true)}
                                        className="rounded-lg bg-slate-700 px-3 py-1 text-xs text-slate-400 hover:bg-red-900/40 hover:text-red-400 transition-colors"
                                    >
                                        Delete Sprint
                                    </button>
                                )}
                                {deleteMutation.isError && (
                                    <span className="text-xs text-red-400">
                                        {deleteMutation.error instanceof Error ? deleteMutation.error.message : 'Failed to delete sprint'}
                                    </span>
                                )}
                            </span>
                        </>
                    ) : prRaised ? (
                        <SprintReleaseVisualizer
                            sprintNumber={sprint.number}
                            releasePrId={releasePrId!}
                            releasePrUrl={releasePrUrl ?? null}
                            pipeline={data.pipeline}
                        />
                    ) : result ? (
                        <span className="text-sm text-emerald-400">
                            ✓ PR #{result.prId} raised{result.deferredCount > 0 ? ` · ${result.deferredCount} deferred` : ''}
                        </span>
                    ) : sprint.status === 'Started' ? (
                        <>
                            {isMySession ? (
                                <button
                                    title="You're running this session in Claude Code"
                                    disabled
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-violet-900/40 px-3 py-1.5 text-xs font-semibold text-violet-400 cursor-not-allowed opacity-70"
                                >
                                    🤖 Your Session
                                </button>
                            ) : sessionInProgress ? (
                                <button
                                    title={`Session in progress by ${sprint.workingDeveloper!.split('@')[0]}`}
                                    disabled
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-500 cursor-not-allowed opacity-60"
                                >
                                    🤖 Claude It!
                                </button>
                            ) : (
                                <button
                                    title="Open Claude Code and run /start-sprint for this sprint"
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-violet-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-600 transition-colors"
                                    onClick={e => { e.stopPropagation(); window.location.href = `marque://start-sprint/${sprint.number}` }}
                                >
                                    🤖 Claude It!
                                </button>
                            )}
                            {sprint.workingDeveloper && (
                                <button
                                    title={isMySession ? "You're running this session — open to see what's happening" : `Live session: ${sprint.workingDeveloper.split('@')[0]}`}
                                    onClick={() => setLiveSessionOpen(true)}
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-900/40 px-3 py-1.5 text-xs font-semibold text-emerald-400 hover:bg-emerald-900/60 transition-colors animate-pulse"
                                >
                                    🔴 Live Session
                                </button>
                            )}
                            <span className="text-xs text-slate-400 italic">
                                Merge items to <span className="font-mono">{sprint.branchName}</span> to unlock release
                            </span>
                            {mergedCount === 0 && (
                                <span className="ml-auto flex items-center gap-2">
                                    {confirmDelete ? (
                                        <>
                                            <span className="text-xs text-red-400">Abandon sprint and un-assign all items?</span>
                                            <button
                                                onClick={() => deleteMutation.mutate()}
                                                disabled={deleteMutation.isPending}
                                                className="rounded-lg bg-red-700 px-3 py-1 text-xs font-semibold text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                                            >
                                                {deleteMutation.isPending ? 'Abandoning…' : 'Confirm'}
                                            </button>
                                            <button
                                                onClick={() => setConfirmDelete(false)}
                                                className="rounded-lg bg-slate-700 px-3 py-1 text-xs text-slate-300 hover:bg-slate-600 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={() => setConfirmDelete(true)}
                                            className="rounded-lg bg-slate-700 px-3 py-1 text-xs text-slate-400 hover:bg-red-900/40 hover:text-red-400 transition-colors"
                                        >
                                            Abandon Sprint
                                        </button>
                                    )}
                                    {deleteMutation.isError && (
                                        <span className="text-xs text-red-400">
                                            {deleteMutation.error instanceof Error ? deleteMutation.error.message : 'Failed to abandon sprint'}
                                        </span>
                                    )}
                                </span>
                            )}
                        </>
                    ) : (
                        <>
                            {sprint.workingDeveloper && (
                                <button
                                    title={isMySession ? "You're running this session — open to see what's happening" : `Live session: ${sprint.workingDeveloper.split('@')[0]}`}
                                    onClick={() => setLiveSessionOpen(true)}
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-900/40 px-3 py-1.5 text-xs font-semibold text-emerald-400 hover:bg-emerald-900/60 transition-colors animate-pulse"
                                >
                                    🔴 Live Session
                                </button>
                            )}
                            <button
                                onClick={() => { setResult(null); addToReleaseMutation.mutate() }}
                                disabled={addToReleaseMutation.isPending || !allResolved}
                                title="Raises a PR from the sprint branch to main. Once CI passes the sprint is merged and the release branch is created automatically."
                                className="rounded-lg bg-emerald-700 px-4 py-1.5 text-sm font-semibold text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {addToReleaseMutation.isPending ? 'Raising PR…' : 'Add Sprint to Release ▶'}
                            </button>
                            {!allResolved && (
                                <span className="text-xs text-slate-400 italic">All items must be Resolved (or deferred) before releasing</span>
                            )}
                            {allResolved && deferIds.size > 0 && (
                                <span className="text-xs text-slate-400">{deferIds.size} item{deferIds.size !== 1 ? 's' : ''} will be deferred</span>
                            )}
                            {addToReleaseMutation.isError && (
                                <span className="text-xs text-red-400">
                                    {addToReleaseMutation.error instanceof Error
                                        ? addToReleaseMutation.error.message
                                        : 'Failed to raise PR'}
                                </span>
                            )}
                            <span className="ml-auto flex items-center gap-2">
                                {confirmDelete ? (
                                    <>
                                        <span className="text-xs text-red-400">Delete sprint and un-assign all items?</span>
                                        <button
                                            onClick={() => deleteMutation.mutate()}
                                            disabled={deleteMutation.isPending}
                                            className="rounded-lg bg-red-700 px-3 py-1 text-xs font-semibold text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                                        >
                                            {deleteMutation.isPending ? 'Deleting…' : 'Confirm'}
                                        </button>
                                        <button
                                            onClick={() => setConfirmDelete(false)}
                                            className="rounded-lg bg-slate-700 px-3 py-1 text-xs text-slate-300 hover:bg-slate-600 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => setConfirmDelete(true)}
                                        className="rounded-lg bg-slate-700 px-3 py-1 text-xs text-slate-400 hover:bg-red-900/40 hover:text-red-400 transition-colors"
                                    >
                                        Delete Sprint
                                    </button>
                                )}
                                {deleteMutation.isError && (
                                    <span className="text-xs text-red-400">
                                        {deleteMutation.error instanceof Error ? deleteMutation.error.message : 'Failed to delete sprint'}
                                    </span>
                                )}
                            </span>
                        </>
                    )}
                </div>
            </div>

            {/* Items list — also a drop target for inter-sprint drag-and-drop */}
            <div
                className={`rounded-xl border bg-slate-800/40 overflow-hidden transition-colors ${
                    isDragOver
                        ? 'border-indigo-500/60 bg-indigo-950/20'
                        : 'border-slate-700'
                }`}
                onDragOver={e => {
                    if (!prRaised && e.dataTransfer.types.includes('application/x-sprint-item')) {
                        e.preventDefault()
                        e.dataTransfer.dropEffect = 'move'
                        setIsDragOver(true)
                    }
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={e => {
                    e.preventDefault()
                    setIsDragOver(false)
                    if (prRaised) { return }
                    const raw = e.dataTransfer.getData('application/x-sprint-item')
                    if (!raw) { return }
                    const { workItemId, fromSprintNumber } = JSON.parse(raw) as { workItemId: number; fromSprintNumber: number }
                    if (fromSprintNumber === data.sprint.number) { return }
                    // Check query cache for dependency info (non-blocking)
                    const cached = queryClient.getQueryData<WorkItemDetail>(['work-item-detail', workItemId])
                    const hasDeps = cached
                        ? ((cached.requires?.length ?? 0) > 0 || (cached.requiredBy?.length ?? 0) > 0)
                        : false
                    if (hasDeps) {
                        setPendingDrop({ workItemId, hasDeps: true })
                    } else {
                        moveItemMutation.mutate(workItemId)
                    }
                }}
            >
                <div className="px-5 py-2 border-b border-slate-700/60 bg-slate-900/40 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Sprint Items</span>
                    <span className="text-[10px] text-slate-600 bg-slate-700/50 rounded-full px-1.5 py-0.5">{totalCount}</span>
                    {isDragOver && (
                        <span className="text-[10px] text-indigo-400 italic">Drop to move here</span>
                    )}
                    {!prRaised && !isDragOver && sprint.status === 'Pushed' && (
                        <span className="text-[10px] text-slate-500 italic">Hover to defer · drag to move between sprints</span>
                    )}
                    {!prRaised && !isDragOver && sprint.status === 'Started' && (
                        <span className="text-[10px] text-slate-500 italic">Drag to move between sprints</span>
                    )}
                    {!prRaised && !sprint.locked && (
                        <button
                            onClick={e => { e.stopPropagation(); setAddItemOpen(true) }}
                            className="text-[10px] text-indigo-400 hover:text-indigo-300 transition-colors"
                        >
                            + Add Item
                        </button>
                    )}
                    <input
                        type="text"
                        placeholder="Search…"
                        value={itemSearch}
                        onChange={e => setItemSearch(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        className="bg-slate-800 border border-slate-600/60 rounded px-2 py-0.5 text-[10px] text-slate-200 placeholder-slate-600 focus:outline-none focus:border-indigo-500 w-28"
                    />
                    <button
                        onClick={toggleItemsExpanded}
                        className="ml-auto text-[10px] text-slate-500 hover:text-slate-300 transition-colors"
                    >
                        {itemsExpanded ? '▾ hide' : '▸ show'}
                    </button>
                </div>
                {itemsExpanded && (
                    <div className="divide-y divide-slate-700/20">
                        {items
                            .filter(item => {
                                if (!itemSearch.trim()) { return true }
                                const q = itemSearch.trim().toLowerCase()
                                return item.title.toLowerCase().includes(q) || String(item.workItemId).includes(q)
                            })
                            .map(item => (
                                <SprintItemRow
                                    key={item.workItemId}
                                    item={item}
                                    deferred={deferIds.has(item.workItemId)}
                                    onToggleDefer={prRaised ? () => {} : toggleDefer}
                                    sprintNumber={data.sprint.number}
                                    dragEnabled={!prRaised}
                                    deferEnabled={!prRaised && sprint.status === 'Pushed'}
                                    {...(!prRaised && !sprint.locked ? { onSendToBacklog: () => removeFromSprintMutation.mutate(item.workItemId) } : {})}
                                />
                            ))}
                    </div>
                )}
            </div>

            {/* Pending drop confirmation — shown when dropped item has dependencies */}
            {pendingDrop && (
                <div className="rounded-xl border border-amber-700/60 bg-amber-950/30 px-5 py-4 flex flex-col gap-3">
                    <p className="text-sm text-amber-300 font-medium">
                        This item has dependencies — move it anyway?
                    </p>
                    <p className="text-xs text-amber-500/80">
                        It requires other items or is required by others. Moving it to this sprint may leave dependencies unresolved.
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                moveItemMutation.mutate(pendingDrop.workItemId)
                                setPendingDrop(null)
                            }}
                            className="rounded-lg bg-amber-700 px-4 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 transition-colors"
                        >
                            Move anyway
                        </button>
                        <button
                            onClick={() => setPendingDrop(null)}
                            className="rounded-lg bg-slate-700 px-4 py-1.5 text-xs text-slate-300 hover:bg-slate-600 transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

        </div>
    )
}

function PlanAnotherSprintSection() {
    const queryClient   = useQueryClient()
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const nextNumberQ   = useQuery({
        queryKey: ['sprint-next-number'],
        queryFn:  releaseApi.getNextSprintNumber,
        staleTime: 60_000,
    })
    const backlogQ = useQuery({
        queryKey: ['releases-backlog'],
        queryFn:  releaseApi.getReleasesBacklog,
        staleTime: 30_000,
        enabled:  open,
    })
    const [sprintNumber, setSprintNumber] = useState<number | null>(null)
    const [selectedIds, setSelectedIds]   = useState<Set<number>>(new Set())
    const resolvedNumber = sprintNumber ?? nextNumberQ.data?.nextNumber ?? 1

    const toggleItem = (id: number) => setSelectedIds(prev => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id); else next.add(id)
        return next
    })

    const planMutation = useMutation({
        mutationFn: () => releaseApi.planSprint({ sprintNumber: resolvedNumber, workItemIds: [...selectedIds] }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['sprint-active'] })
            void queryClient.invalidateQueries({ queryKey: ['sprint-next-number'] })
            void queryClient.invalidateQueries({ queryKey: ['releases-backlog'] })
            setSelectedIds(new Set())
        },
    })

    useEffect(() => {
        if (!planMutation.isSuccess) return
        const t = setTimeout(() => { planMutation.reset() }, 2000)
        return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planMutation.isSuccess])

    const startMutation = useMutation({
        mutationFn: () => releaseApi.startSprint(resolvedNumber),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['sprint-active'] })
            void queryClient.invalidateQueries({ queryKey: ['sprint-next-number'] })
        },
    })

    const anyError = planMutation.error ?? startMutation.error
    const backlogItems = (backlogQ.data?.items ?? []).filter(
        i => !i.iterationPath || !SPRINT_PATH_PATTERN.test(i.iterationPath)
    )
    const filteredBacklogItems = search
        ? backlogItems.filter(i => i.title.toLowerCase().includes(search.toLowerCase()) || String(i.id).includes(search))
        : backlogItems

    return (
        <div className="rounded-xl border border-slate-700/40 bg-slate-800/20">
            <button
                className="w-full cursor-pointer select-none px-5 py-3 text-sm font-medium text-slate-500 hover:text-slate-300 transition-colors flex items-center gap-2"
                onClick={() => setOpen(v => !v)}
            >
                <span className="text-xs">{open ? '▾' : '▶'}</span> Plan another sprint…
            </button>
            {open && (
                <div className="px-5 pb-4 pt-2 flex flex-col gap-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        <label className="text-sm text-slate-400 shrink-0">Sprint number</label>
                        <input
                            type="number"
                            min={1}
                            value={resolvedNumber}
                            onChange={e => setSprintNumber(Math.max(1, parseInt(e.target.value) || 1))}
                            className="w-20 rounded-lg bg-slate-800 border border-slate-600 px-3 py-1.5 text-sm text-slate-100 text-center focus:outline-none focus:border-indigo-500"
                        />
                        <button
                            onClick={() => planMutation.mutate()}
                            disabled={planMutation.isPending || planMutation.isSuccess}
                            className="rounded-lg bg-slate-700 px-4 py-1.5 text-sm font-medium text-slate-200 hover:bg-slate-600 transition-colors disabled:opacity-50"
                        >
                            {planMutation.isPending ? 'Planning…' : planMutation.isSuccess ? 'Planned ✓' : `Plan Sprint${selectedIds.size > 0 ? ` (${selectedIds.size} items)` : ''}`}
                        </button>
                        <button
                            onClick={() => startMutation.mutate()}
                            disabled={startMutation.isPending}
                            className="rounded-lg bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-indigo-500 transition-colors disabled:opacity-50"
                        >
                            {startMutation.isPending ? 'Starting…' : 'Start Sprint ▶'}
                        </button>
                    </div>
                    {backlogItems.length > 0 && (
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2 mb-1">
                                <p className="text-xs text-slate-500 shrink-0">Select items to include:</p>
                                <input
                                    type="text"
                                    placeholder="Search by # or title…"
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    className="flex-1 bg-slate-900/60 border border-slate-600 rounded-md px-2 py-0.5 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                                />
                            </div>
                            <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-700 bg-slate-800/40 divide-y divide-slate-700/30">
                                {filteredBacklogItems.map(item => (
                                    <label
                                        key={item.id}
                                        className="flex items-center gap-3 px-3 py-2 hover:bg-slate-700/30 cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(item.id)}
                                            onChange={() => toggleItem(item.id)}
                                            className="accent-indigo-500 shrink-0"
                                        />
                                        <span className="font-mono text-xs font-bold text-indigo-400 shrink-0 w-14">#{item.id}</span>
                                        <span className="text-xs text-slate-200 truncate flex-1 min-w-0">{item.title}</span>
                                        <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${typeStyle(item.workItemType)}`}>{item.workItemType}</span>
                                    </label>
                                ))}
                                {filteredBacklogItems.length === 0 && (
                                    <div className="px-3 py-3 text-center text-xs text-slate-500">No items match your search.</div>
                                )}
                            </div>
                        </div>
                    )}
                    {backlogQ.isLoading && (
                        <p className="text-xs text-slate-500 animate-pulse">Loading backlog…</p>
                    )}
                    {anyError && (
                        <p className="text-red-400 text-xs">
                            {anyError instanceof Error ? anyError.message : 'Operation failed'}
                        </p>
                    )}
                </div>
            )}
        </div>
    )
}

function SprintPanel() {
    const { data: sprints, isLoading, isError } = useQuery({
        queryKey: ['sprint-active'],
        queryFn: releaseApi.getActiveSprints,
        staleTime: 30_000,
        refetchInterval: 30_000,
    })

    const { data: me } = useQuery({
        queryKey: ['me'],
        queryFn: releaseApi.getMe,
        staleTime: Infinity,
    })

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <span className="text-slate-500 text-sm animate-pulse">Loading sprint…</span>
            </div>
        )
    }

    if (isError) {
        return (
            <div className="rounded-xl border border-red-800 bg-red-950/50 p-5 text-red-300 text-sm">
                Failed to load sprint status.
            </div>
        )
    }

    if (!sprints || sprints.length === 0) {
        return <NoSprintPanel />
    }

    const sorted = [...sprints].sort((a, b) => a.sprint.number - b.sprint.number)

    // A Queued sprint is blocked from starting if any other sprint is currently
    // in-flight (Started or Pushed = branch exists, PR not yet merged to main).
    const inFlightSprint = sorted.find(
        s => s.sprint.status === 'Started' || s.sprint.status === 'Pushed'
    )

    return (
        <div className="flex flex-col gap-8">
            {sorted.map((s, i) => {
                const blockedBy = s.sprint.status === 'Queued' && inFlightSprint && inFlightSprint.sprint.number !== s.sprint.number
                    ? inFlightSprint.sprint.number
                    : undefined
                return (
                    <div key={s.sprint.number}>
                        {i > 0 && <div className="border-t border-slate-700/50 -mt-4" />}
                        <SingleSprintPanel data={s} {...(blockedBy !== undefined ? { blockedBySprintNumber: blockedBy } : {})} {...(me?.email ? { currentUserEmail: me.email } : {})} />
                    </div>
                )
            })}
            <PlanAnotherSprintSection />
        </div>
    )
}

// ── Dev ArgoCD sync banner ────────────────────────────────────────────────────

function devSyncSummary(data: ReleaseDevSyncStatus | undefined): {
    state: 'running' | 'out-of-sync' | 'error' | 'synced' | 'unknown'
    label: string
    elapsedSec: number
} {
    if (!data || data.apps.length === 0) {
        return { state: 'unknown', label: 'No dev apps found', elapsedSec: 0 }
    }

    const running   = data.apps.filter(a => a.operationPhase === 'Running')
    const errored   = data.apps.filter(a => a.operationPhase === 'Failed' || a.operationPhase === 'Error')
    const outOfSync = data.apps.filter(a => a.syncStatus === 'OutOfSync')
    const allSynced = data.apps.every(a => a.syncStatus === 'Synced' && a.healthStatus === 'Healthy')

    const elapsed   = Math.round((Date.now() - new Date(data.fetchedAt).getTime()) / 1000)

    if (running.length > 0) {
        return { state: 'running', label: `Syncing ${running.length} app${running.length !== 1 ? 's' : ''}…`, elapsedSec: elapsed }
    }
    if (errored.length > 0) {
        return { state: 'error', label: `Sync failed on ${errored.length} app${errored.length !== 1 ? 's' : ''}`, elapsedSec: elapsed }
    }
    if (outOfSync.length > 0) {
        return { state: 'out-of-sync', label: `ArgoCD detected ${outOfSync.length} change${outOfSync.length !== 1 ? 's' : ''}, waiting to sync`, elapsedSec: elapsed }
    }
    if (allSynced) {
        return { state: 'synced', label: 'Dev synced & healthy', elapsedSec: elapsed }
    }
    return { state: 'unknown', label: 'Checking…', elapsedSec: elapsed }
}

function DevSyncBanner() {
    const { data } = useQuery({
        queryKey: ['release-dev-sync'],
        queryFn:  releaseApi.getReleaseDevSyncStatus,
        // Poll quickly when out-of-sync or actively syncing; back off when settled
        refetchInterval: (query) => {
            const d = query.state.data as ReleaseDevSyncStatus | undefined
            if (!d) { return 15_000 }
            const needsFast = d.apps.some(
                a => a.operationPhase === 'Running' || a.syncStatus === 'OutOfSync'
                  || a.operationPhase === 'Failed'  || a.operationPhase === 'Error'
            )
            return needsFast ? 15_000 : 60_000
        },
        staleTime: 10_000,
    })

    const summary = devSyncSummary(data)

    // Don't show the banner when everything is fine
    if (summary.state === 'synced' || summary.state === 'unknown') { return null }

    const colours: Record<string, string> = {
        running:    'border-amber-700/40 bg-amber-950/30 text-amber-300',
        'out-of-sync': 'border-sky-700/40 bg-sky-950/30 text-sky-300',
        error:      'border-red-700/40 bg-red-950/30 text-red-300',
    }
    const cls = colours[summary.state] ?? 'border-slate-700 bg-slate-800/40 text-slate-300'

    return (
        <div className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm ${cls}`}>
            {summary.state === 'running' && (
                <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
            )}
            {summary.state === 'error' && (
                <span className="shrink-0">⚠</span>
            )}
            {summary.state === 'out-of-sync' && (
                <span className="shrink-0">⏳</span>
            )}
            <span>{summary.label}</span>
            <span className="ml-auto text-xs opacity-60">dev · ArgoCD</span>
        </div>
    )
}

// ── Pending-release queue panel (AB#2100) ────────────────────────────────────

function queueAgeLabel(ageMinutes: number): string {
    if (ageMinutes < 1) return 'just now'
    if (ageMinutes < 60) return `${Math.round(ageMinutes)}m`
    const hrs = ageMinutes / 60
    if (hrs < 24) return `${Math.round(hrs)}h`
    return `${Math.round(hrs / 24)}d`
}

function PendingQueueRow({ entry, onRetry, isRetrying }: {
    entry: PendingQueueEntry
    onRetry: (prId: number) => void
    isRetrying: boolean
}) {
    const badge: Record<PendingQueueEntry['state'], { cls: string; label: string }> = {
        pending:     { cls: 'bg-slate-500/15 text-slate-300 ring-1 ring-slate-500/30',  label: 'awaiting merge' },
        'ci-failed': { cls: 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30',  label: `CI failing ×${entry.retryCount}` },
        flagged:     { cls: 'bg-red-500/15 text-red-400 ring-1 ring-red-500/30',        label: 'flagged — polling stopped' },
    }
    const b = badge[entry.state]

    return (
        <div className="flex items-center gap-3 px-4 py-2 text-sm">
            <span className="font-mono text-xs font-bold text-indigo-400 shrink-0 w-16">PR #{entry.prId}</span>
            <span className="flex-1 truncate text-slate-200">{entry.label}</span>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${b.cls}`}>
                {b.label}
            </span>
            <span className="shrink-0 w-14 text-right text-xs text-slate-500" title={`${entry.ageMinutes} minutes in queue`}>
                {queueAgeLabel(entry.ageMinutes)}
            </span>
            {entry.state === 'flagged' && (
                <button
                    onClick={() => onRetry(entry.prId)}
                    disabled={isRetrying}
                    className="shrink-0 rounded-lg border border-slate-700 px-2.5 py-1 text-[11px] font-medium text-slate-300 hover:bg-slate-700 transition-colors disabled:opacity-40"
                >
                    {isRetrying ? 'Retrying…' : 'Retry'}
                </button>
            )}
        </div>
    )
}

function PendingQueuePanel() {
    const queryClient = useQueryClient()
    const { data } = useQuery({
        queryKey: ['releases-pending-queue'],
        queryFn: releaseApi.getPendingReleases,
        // Poll faster while anything is queued; back off when the queue is empty.
        refetchInterval: (query) => {
            const d = query.state.data as PendingQueueResponse | undefined
            return d && d.items.length > 0 ? 15_000 : 60_000
        },
        staleTime: 10_000,
    })

    const retry = useMutation({
        mutationFn: (prId: number) => releaseApi.retryPendingRelease(prId),
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['releases-pending-queue'] }),
    })

    if (!data || data.items.length === 0) return null

    const flaggedCount = data.items.filter(i => i.state === 'flagged').length

    return (
        <div className={`rounded-lg border ${flaggedCount > 0 ? 'border-red-800/60' : 'border-slate-700'} bg-slate-900/40 overflow-hidden`}>
            <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-2">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Pending release queue
                </span>
                <span className="text-xs text-slate-600">
                    {data.items.length} PR{data.items.length !== 1 ? 's' : ''} awaiting completion
                </span>
                {flaggedCount > 0 && (
                    <span className="ml-auto text-xs font-medium text-red-400">
                        {flaggedCount} flagged — needs attention
                    </span>
                )}
            </div>
            <div className="divide-y divide-slate-800/60">
                {data.items.map(entry => (
                    <PendingQueueRow
                        key={entry.prId}
                        entry={entry}
                        isRetrying={retry.isPending && retry.variables === entry.prId}
                        onRetry={(prId) => retry.mutate(prId)}
                    />
                ))}
            </div>
        </div>
    )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function ReleasesPage() {
    const [tab,      setTab]      = useState<TabId>(() => (localStorage.getItem('releases-tab') as TabId | null) ?? 'backlog')
    const [viewMode, setViewMode] = useState<ViewMode>(() => (localStorage.getItem('releases-viewmode') as ViewMode | null) ?? 'list')

    const setTabPersisted      = (t: TabId)    => { setTab(t);      localStorage.setItem('releases-tab',      t) }
    const setViewModePersisted = (m: ViewMode) => { setViewMode(m); localStorage.setItem('releases-viewmode', m) }

    // Prefetch all panels so tab switching is instant.
    const queryClient = useQueryClient()
    useEffect(() => {
        void queryClient.prefetchQuery({ queryKey: ['releases-parking-lot'],  queryFn: releaseApi.getReleasesParkingLot })
        void queryClient.prefetchQuery({ queryKey: ['releases-backlog'],     queryFn: releaseApi.getReleasesBacklog })
        void queryClient.prefetchQuery({ queryKey: ['releases-in-progress'], queryFn: releaseApi.getReleasesInProgress })
        void queryClient.prefetchQuery({ queryKey: ['releases-ready'],       queryFn: releaseApi.getReleasesReady })
        void queryClient.prefetchQuery({ queryKey: ['releases-active'],      queryFn: releaseApi.getActiveRelease })
        void queryClient.prefetchQuery({ queryKey: ['releases-history'],     queryFn: releaseApi.getReleaseHistory })
        void queryClient.prefetchQuery({ queryKey: ['sprint-history', 'recent'], queryFn: () => releaseApi.getSprintHistory(10) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const isWorkItemTab = tab === 'parking-lot' || tab === 'backlog' || tab === 'in-progress' || tab === 'ready'

    return (
        <WorkItemModalProvider>
        <AreaOverrideProvider>
        <div className="flex flex-col min-h-0 flex-1">
            <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
                    <div>
                        <span className="text-sm font-semibold text-slate-200">Work Items</span>
                        <PageDescription subtitle="Work items · branch lifecycle" storageKey="desc-releases" detail="Tracks work items from backlog through to release. Parking Lot holds items awaiting a spec; Backlog is ready to start; In Progress has an active branch; Ready to Release has been resolved and is waiting to ship. The Sprint tab manages sprint-based releases to main." />
                    </div>

                    {/* View mode toggle (#307) */}
                    <div className="flex items-center gap-1 bg-slate-800 border border-slate-700 rounded-lg p-0.5">
                        <button
                            onClick={() => setViewModePersisted('list')}
                            className={[
                                'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                                viewMode === 'list'
                                    ? 'bg-slate-700 text-slate-100'
                                    : 'text-slate-400 hover:text-slate-200',
                            ].join(' ')}
                        >
                            ☰ List
                        </button>
                        <button
                            onClick={() => setViewModePersisted('board')}
                            className={[
                                'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                                viewMode === 'board'
                                    ? 'bg-slate-700 text-slate-100'
                                    : 'text-slate-400 hover:text-slate-200',
                            ].join(' ')}
                        >
                            ▦ Board
                        </button>
                    </div>
                </div>
            </header>

            <main className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
                {viewMode === 'board' ? (
                    /* ── Board / Kanban view ─────────────────────────────── */
                    <div className="flex flex-col gap-4">
                        <KanbanView />
                    </div>
                ) : (
                    /* ── List view (tabs) ────────────────────────────────── */
                    <div className="max-w-7xl mx-auto flex flex-col gap-5">
                        {/* Tabs */}
                        <div className="flex items-center gap-1 border-b border-slate-800 pb-0">
                            {TABS.map(t => (
                                <button
                                    key={t.id}
                                    onClick={() => setTabPersisted(t.id)}
                                    className={[
                                        'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                                        tab === t.id
                                            ? t.id === 'parking-lot'
                                                ? 'border-amber-500 text-amber-300'
                                                : 'border-indigo-500 text-white'
                                            : 'border-transparent text-slate-400 hover:text-slate-200',
                                    ].join(' ')}
                                >
                                    {t.id === 'parking-lot' && (
                                        <span className="mr-1.5 text-amber-500/70">⊘</span>
                                    )}
                                    {t.label}
                                </button>
                            ))}
                        </div>

                        {/* Area badge legend — shown on work-item tabs only */}
                        {isWorkItemTab && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-1">
                                <span className="text-xs text-slate-600 uppercase tracking-wider font-medium shrink-0">Area</span>
                                {[
                                    { label: 'Ops',        style: 'bg-violet-500/20 text-violet-300 ring-1 ring-violet-500/30' },
                                    { label: 'Central',    style: 'bg-blue-500/20 text-blue-300 ring-1 ring-blue-500/30' },
                                    { label: 'Tenant',     style: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30' },
                                ].map(b => (
                                    <span key={b.label} className="flex items-center gap-1.5">
                                        <span className={`inline-block w-2 h-2 rounded-sm ${b.style.split(' ')[0]}`} />
                                        <span className="text-xs text-slate-500">{b.label}</span>
                                    </span>
                                ))}
                                <span className="text-slate-700 select-none">·</span>
                                <span className="text-xs text-slate-600 uppercase tracking-wider font-medium shrink-0">Module</span>
                                {[
                                    { label: 'Finance',    style: 'bg-amber-500/20' },
                                    { label: 'Sales',      style: 'bg-indigo-500/20' },
                                    { label: 'Purchasing', style: 'bg-orange-500/20' },
                                    { label: 'Inventory',  style: 'bg-lime-500/20' },
                                    { label: 'PLM',        style: 'bg-pink-500/20' },
                                    { label: 'Auth',       style: 'bg-slate-500/20' },
                                ].map(b => (
                                    <span key={b.label} className="flex items-center gap-1.5">
                                        <span className={`inline-block w-2 h-2 rounded-sm ${b.style}`} />
                                        <span className="text-xs text-slate-500">{b.label}</span>
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* ArgoCD dev sync visibility gap banner */}
                        <DevSyncBanner />

                        {/* Pending-release PR queue (AB#2100) */}
                        <PendingQueuePanel />

                        {/* Active tab panel */}
                        {isWorkItemTab && <TabPanel key={tab} tab={tab} />}
                        {tab === 'sprint'  && <SprintPanel />}
                        {tab === 'history' && <HistoryPanel />}
                    </div>
                )}
            </main>
        </div>
        </AreaOverrideProvider>
        </WorkItemModalProvider>
    )
}
